import { useState, useEffect, useCallback, useMemo, useRef }
  from "react";
import {
  Home, BookOpen, Play, Check, Circle,
  Loader2, ChevronLeft, ChevronRight, FileText, AlertTriangle, ClipboardList, Library, X,
} from "lucide-react";
import "../Education.css";
import { renderBlocks, renderInline } from "../lib/markdown";

/* ============================================================ "I'M STUCK"
   Randall D23, 2026-09-10, after Alton Labrecque phoned stuck on a 56 degree wedge at 3
   degrees flat and could not find the formula anywhere he could reach it.

   ALWAYS PASS attemptId WHEN THERE IS ONE. It is not decoration: the server uses it to
   decide whether this fitter is mid-graded-quiz, and therefore whether the answer key is
   even loaded into the model's context. Drop it and a live quiz looks like idle browsing,
   and the guardrail that makes the PGA credit mean something quietly stops applying. */
function StuckHelp({ lessonId, screenIndex, itemId, attemptId }) {
  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState("");
  const [busy, setBusy] = useState(false);
  const [answer, setAnswer] = useState(null);
  const [mode, setMode] = useState(null);
  const [err, setErr] = useState("");

  async function ask() {
    setBusy(true); setErr(""); setAnswer(null);
    try {
      const r = await api("/stuck", {
        method: "POST",
        body: JSON.stringify({
          question: (question || "").slice(0, 600),
          lesson_id: lessonId || null,
          screen_index: typeof screenIndex === "number" ? screenIndex : null,
          item_id: itemId || null,
          attempt_id: attemptId || null,
        }),
      });
      setAnswer(r.answer || "");
      setMode(r.mode || null);
    } catch (e) {
      setErr(e.message || "Could not reach the tutor just now.");
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <button type="button" className="edu-stuck-open" data-testid="edu-stuck-open"
        onClick={() => setOpen(true)}>
        I&rsquo;m stuck
      </button>
    );
  }

  return (
    <aside className="edu-stuck" data-testid="edu-stuck">
      <div className="edu-stuck-head">
        <span className="edu-stuck-label">Ask CoFitter about this screen</span>
        <button type="button" className="edu-stuck-close" onClick={() => setOpen(false)}
          aria-label="Close">Close</button>
      </div>

      {/* Said BEFORE they ask, not after they are refused. Being told the rule up front
          is help; being refused once you have asked feels like a door in the face. */}
      {attemptId && (
        <p className="edu-stuck-note">
          You&rsquo;re in a graded quiz, so I&rsquo;ll explain the method and work a
          different example &mdash; but I won&rsquo;t give you this answer.
        </p>
      )}

      <textarea className="edu-stuck-input" rows={2} maxLength={600} value={question}
        placeholder="What&rsquo;s confusing? (optional)"
        onChange={(e) => setQuestion(e.target.value)}
        data-testid="edu-stuck-input" />

      <div className="edu-stuck-actions">
        <button type="button" className="edu-btn" onClick={ask} disabled={busy}
          data-testid="edu-stuck-ask">
          {busy ? "Thinking\u2026" : answer ? "Ask again" : "Help me with this"}
        </button>
      </div>

      {err && <p className="edu-stuck-err" role="alert">{err}</p>}

      {answer && (
        <div className="edu-stuck-answer" data-testid="edu-stuck-answer">
          {renderBlocks(answer)}
          {mode === "graded" && (
            <p className="edu-stuck-foot">Answer withheld &mdash; graded quiz.</p>
          )}
        </div>
      )}
    </aside>
  );
}

// HG Education — the student shell (Phase 1).
// Four screens, built to HG_EDUCATION_PLATFORM_DESIGN_v1.html:
//   My Learning · Curriculum · Lesson Player · Quiz
// Design tokens come from App.css (hub dark) — this file adds no new palette.
// Numbers are always DM Sans: BN 3rd Place has no numerals.

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

async function api(path, opts = {}) {
  const res = await fetch(`${API_URL}/api/education${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    ...opts,
  });
  if (!res.ok) {
    const d = await res.json().catch(() => ({}));
    // The status has to survive: a submit that fails with 400 "already submitted" is
    // recoverable by fetching the result, and a network failure is not.
    // A STRUCTURED REFUSAL (2026-09-28) -- {code, message, body} -- keeps its parts on the error;
    // `new Error(object)` used to print "[object Object]".
    const detail = d.detail;
    const text = (detail && typeof detail === "object") ? (detail.message || "") : detail;
    const e = new Error(text || `Request failed (${res.status})`);
    e.status = res.status;
    if (detail && typeof detail === "object") e.detail = detail;
    throw e;
  }
  return res.json();
}

// Where a quiz attempt id is parked so a refresh mid-quiz resumes instead of losing the
// attempt. sessionStorage, not localStorage: it should not outlive the tab.
const ATTEMPT_KEY = "hg-edu-attempt";
const attemptStore = {
  key: (spec) => `${ATTEMPT_KEY}:${spec.kind || "chapter_quiz"}:${spec.chapterId || spec.trackId}`,
  get(spec) {
    try { return window.sessionStorage.getItem(this.key(spec)) || ""; } catch { return ""; }
  },
  set(spec, id) {
    try { window.sessionStorage.setItem(this.key(spec), id); } catch { /* private mode */ }
  },
  clear(spec) {
    try {
      const id = this.get(spec);
      window.sessionStorage.removeItem(this.key(spec));
      if (id) window.sessionStorage.removeItem(`${ATTEMPT_KEY}:answers:${id}`);
    } catch { /* private mode */ }
  },
  // Resuming the ATTEMPT but not the ANSWERS would still lose the student's work, which is
  // the thing a refresh must not do. Keyed by attempt id so a new attempt starts clean.
  getAnswers(id) {
    try {
      return JSON.parse(window.sessionStorage.getItem(`${ATTEMPT_KEY}:answers:${id}`) || "{}");
    } catch { return {}; }
  },
  setAnswers(id, answers) {
    try {
      window.sessionStorage.setItem(`${ATTEMPT_KEY}:answers:${id}`, JSON.stringify(answers));
    } catch { /* private mode or full */ }
  },
};

// The answers state is { item_id: { key | keys | num } }; the server speaks the submit shape.
// ONLY ITEMS THE STUDENT HAS TOUCHED ARE SENT. An item absent from a submit is filled from the
// server's checkpointed copy, so an answer this tab lost is not overwritten by a blank; a
// cleared box is still an entry here and still submits NULL.
function toResponses(items, answers) {
  return (items || []).filter((it) => answers[it.item_id]).map((it) => {
    const a = answers[it.item_id];
    return {
      item_id: it.item_id,
      chosen_key: a.key || "",
      chosen_keys: a.keys || [],
      // A cleared box must submit NULL, not "" and not 0 — "" grades as a wrong
      // answer of zero, which is not what an unanswered question means.
      numeric_response: (a.num === "" || a.num === undefined || a.num === null)
        ? null : Number(a.num),
    };
  });
}

function fromSaved(saved) {
  const out = {};
  for (const r of saved || []) {
    if (!r || !r.item_id) continue;
    if (r.chosen_keys && r.chosen_keys.length) out[r.item_id] = { keys: r.chosen_keys };
    else if (r.chosen_key) out[r.item_id] = { key: r.chosen_key };
    else if (r.numeric_response !== null && r.numeric_response !== undefined) {
      out[r.item_id] = { num: String(r.numeric_response) };
    }
  }
  return out;
}

const CHECKPOINT_MS = 15000;      // E.CHECKPOINT_INTERVAL_SECONDS on the server
const CHECKPOINT_DEBOUNCE_MS = 1500;

const mmss = (secs) => {
  const s = Math.max(0, Math.floor(secs || 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

// SCREENS THAT WILL NEVER CARRY AUDIO, so they must not announce that audio is coming.
//
// A knowledge check has no narration by design: `narration_text.narrated_text()` returns ""
// for these types, so no clip is ever generated and `attach_audio` never finds a slug for
// them. The per-screen pending banner keyed on "no audio_slug", which is true of a knowledge
// check permanently — so every check screen in chapters 1-3 told the student its narration was
// in production, for a recording that is never going to exist.
//
// MIRRORS narration_text.NO_NARRATION_TYPES. If a type is added there it belongs here too;
// the failure mode is cosmetic in both directions, which is why this is a small duplicated
// set rather than another field on the payload.
const NEVER_NARRATED = new Set(["knowledge_check", "formative_practice"]);

// A DATE A STUDENT READS, not an ISO stamp. This is their own progress list, not a log.
//
// THE FULL STRING IS PARSED, never a sliced `YYYY-MM-DD`. `new Date("2026-08-25")` is parsed
// as UTC midnight and then rendered in local time, so in Coeur d'Alene (UTC-6) it would print
// the 24th — a student's completion silently dated a day early. The stored value carries its
// offset; handing the whole thing to Date keeps the day right.
// Locale is deliberately the viewer's, not a hard-coded one.
const fmtDay = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso).slice(0, 10);
  try {
    return d.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  } catch { return String(iso).slice(0, 10); }
};

// THE PASS MARK IS SHOWN AS WHAT IT IS (Randall 2026-10-02): Henry-Griffitts's own standard. It used to carry a
// "provisional" tag and a sentence under every result; both are gone. Nothing is put in their place: the mark is the
// mark, and the app makes no claim about how it was arrived at.

// "Nothing was entered", for ONE review row. Round 4 ruling 3 fixed the server's counters, so
// the totals now come from the server; this stays because a row's own display is a different
// question from the tally, and it needs no round trip to answer.
const unanswered = (rv) => rv.your_answer === null || rv.your_answer === undefined
  || rv.your_answer === "";

// THE MARK HE MUST MAKE, IN THE SERVER'S WORDS (Randall, 2026-10-05). "85% to pass" on a 30-question final misled: the
// mark is 26 of 30. The server sends `pass_mark_label` ("26 of 30 correct", "7 of 8 correct"); the percentage is only
// the fallback for an attempt the server has not labelled.
function PassMark({ pct, label }) {
  if (label) {
    return (
      <span className="edu-passmark" data-testid="edu-passmark">
        <b className="num">{label}</b> to pass
      </span>
    );
  }
  if (pct === null || pct === undefined) return null;
  return (
    <span className="edu-passmark" data-testid="edu-passmark">
      <b className="num">{pct}%</b> to pass
    </span>
  );
}

// The diagrams travel INSIDE the item payload as SVG text (Round 4 ruling 1), never as a URL:
// the 27 diagnostic diagrams ARE the case library, and a static mount would serve them to
// anyone with the link. So there is no image request to make and nothing to 404 — either the
// payload carried the figure or it did not.
//
// They are our own generated files, but this is still markup being injected, so strip anything
// that could execute or fetch before it renders.
function sanitiseSvg(svg) {
  if (typeof svg !== "string") return "";
  const s = svg.trim();
  if (!s.startsWith("<svg")) return "";        // an SVG document, not arbitrary markup
  return s
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<foreignObject[\s\S]*?<\/foreignObject>/gi, "")
    .replace(/\son\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, "")        // inline handlers
    .replace(/(?:xlink:href|href)\s*=\s*("[^"]*"|'[^']*')/gi,
      (m, val) => (/^["']#/.test(val) ? m : ""))                     // in-document refs only
    .replace(/javascript:/gi, "");
}

// The lesson body, as a student should see it (P0, 2026-08-11).
//
// It is handed ONE SCREEN's `learning_point` (CORRECTED 2026-08-22 — it used to be handed the
// whole lesson's `narration_script` as an on-page transcript, which is no longer rendered).
// The text arrives as prose paragraphs, with a "Sources: ..." line and a
// narration-in-production note appended by the server. Those two are pulled out and styled — a
// citation should look like a citation, and a production status should not read as lesson content.
// Everything else renders as paragraphs. No template markers reach this component any more,
// because the server no longer sends them; if one ever did it would show up as ordinary text and
// the backend test asserting SCAFFOLD_MARKERS would already have failed.
/**
 * A MANUAL FIGURE ON A LESSON SCREEN — treatment A.
 *
 * The image is a byte-for-byte copy of the 300 dpi extraction from the 2016 manual. Everything
 * that makes it look like a printed figure — the white plate, the thin border, the caption
 * beneath — is CSS around it. Nothing here can alter what the diagram says, which is the point:
 * the lie-mark set and the kick-point chart get proper brand artwork later, and until then the
 * originals are shown unretouched rather than approximated.
 */
function LessonFigure({ url, slug, caption, displayWidth }) {
  // A GATED FIGURE COMES THROUGH THE API, NOT FROM /figures/. The lie-mark case figures are
  // not public -- Randall's ruling 2026-08-20 -- so they resolve by manifest lookup behind
  // EducationOnlyMiddleware, exactly as the narration audio does. `crossOrigin` because the
  // request is cross-origin and the route is gated on the auth cookie.
  const src = slug ? `${API_URL}/api/education/media/${slug}` : url;
  if (!src) return null;
  // SHOWN NO LARGER THAN ITS REAL DETAIL (feedback #35, Randall 2026-10-16). A screen may carry
  // `image_display_width` (CSS px): the photo on ch1 L4 s1 holds about 260 px of real detail in an 800 px file and was
  // drawn ~490 px wide, so it read as blurred. With the field the image is drawn at most that wide, centred on its
  // plate; without it, nothing changes.
  const w = Number(displayWidth) || 0;
  const sized = w > 0 ? { style: { maxWidth: `${w}px`, marginLeft: "auto", marginRight: "auto" } } : {};
  return (
    <figure className="edu-plate" data-testid="edu-figure">
      <img src={src} alt={caption || "Figure from the Henry-Griffitts manual"}
           loading="lazy" {...sized} {...(slug ? { crossOrigin: "use-credentials" } : {})} />
      {caption && <figcaption>{caption}</figcaption>}
    </figure>
  );
}

/**
 * A COURSE VIDEO ON A LESSON SCREEN.
 *
 * `controls` and NO `autoPlay`. Deliberate on both counts: a film that starts itself while a
 * student is reading is an interruption, and more importantly PLAYING IS NOT PROGRESS. Nothing
 * here calls the completion endpoint - completion stays an explicit button, and the server's
 * minimum-time gate still has to be satisfied whether the video was watched or not. There is
 * no onEnded handler for exactly that reason.
 *
 * `preload="metadata"` fetches the header only, so a lesson with three films does not pull
 * three files off the volume before the student asks for one.
 */
/**
 * NARRATION FOR ONE SCREEN, IN RANDY HENRY'S VOICE.
 *
 * NARRATION NEVER GATES PROGRESSION. There is no onEnded handler and nothing here touches the
 * completion endpoint — the server's minimum-time gate governs progression and this must not
 * add a second, quieter gate. A fitter who reads faster than Randy talks is not held back, and
 * a fitter with no audio output is not locked out.
 *
 * A MISSING FILE IS NOT AN ERROR. No slug means no player: the screen renders exactly as it
 * did before narration existed. That is why this returns null rather than an empty shell or a
 * "coming soon" — a broken player is worse than no player.
 *
 * Position is remembered per screen so a fitter can leave and resume, in sessionStorage rather
 * than localStorage: it should not outlive the tab, for the same reason the quiz attempt key
 * does not.
 */
const SPEEDS = [1, 1.25, 1.5];

// THE SPEED IS THE STUDENT'S, NOT THE SCREEN'S (Wave 2 item 2). The player mounts afresh on
// every screen, so the speed used to live and die with one screen: pick 1.5x, press Next, and
// the button read 1x again and the clip played at 1x. It is now one per-viewer preference,
// kept in localStorage (a convenience, not a record -- losing it costs a click), and applied to
// every clip as it loads. The resume position stays per screen, in sessionStorage, as before.
const SPEED_KEY = "edu:narration:speed";
const readSpeed = () => {
  try {
    const v = parseFloat(window.localStorage.getItem(SPEED_KEY) || "");
    return SPEEDS.includes(v) ? v : 1;
  } catch { return 1; }
};

function LessonAudio({ slug, title }) {
  const ref = useRef(null);
  const [speed, setSpeedState] = useState(readSpeed);
  const setSpeed = (s) => {
    setSpeedState(s);
    try { window.localStorage.setItem(SPEED_KEY, String(s)); } catch { /* private mode */ }
  };
  const key = slug ? `edu:narration:pos:${slug}` : null;

  useEffect(() => {
    const el = ref.current;
    if (!el || !key) return undefined;
    try {
      const saved = parseFloat(window.sessionStorage.getItem(key) || "0");
      if (saved > 0 && Number.isFinite(saved)) el.currentTime = saved;
    } catch { /* private mode */ }
    const save = () => {
      try {
        // Do not remember the very end: resuming at 0:00 next time is friendlier than
        // resuming half a second before silence.
        const t = el.currentTime;
        if (t > 1 && el.duration && t < el.duration - 1) {
          window.sessionStorage.setItem(key, String(t));
        } else {
          window.sessionStorage.removeItem(key);
        }
      } catch { /* private mode */ }
    };
    el.addEventListener("pause", save);
    el.addEventListener("timeupdate", save);
    return () => {
      save();
      el.removeEventListener("pause", save);
      el.removeEventListener("timeupdate", save);
    };
  }, [key]);

  // Applied now AND whenever a clip loads: loading a source resets playbackRate to
  // defaultPlaybackRate, so setting only playbackRate once would be undone by the next load.
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const apply = () => { el.defaultPlaybackRate = speed; el.playbackRate = speed; };
    apply();
    el.addEventListener("loadedmetadata", apply);
    el.addEventListener("play", apply);
    return () => {
      el.removeEventListener("loadedmetadata", apply);
      el.removeEventListener("play", apply);
    };
  }, [speed, slug]);

  if (!slug) return null;                 // no audio for this screen — render nothing at all
  const src = `${API_URL}/api/education/media/${slug}`;
  return (
    <div className="edu-narration" data-testid="edu-narration">
      <span className="edu-narration-label">Narration</span>
      <audio ref={ref} controls preload="none"
             /* the route is gated on the auth cookie and this request is cross-origin */
             crossOrigin="use-credentials"
             src={src}
             aria-label={title ? `Narration: ${title}` : "Lesson narration"}
             style={{ flex: 1, minWidth: 0 }} />
      <div className="edu-speed" role="group" aria-label="Playback speed">
        {SPEEDS.map((s) => (
          <button key={s} type="button"
                  className={`edu-speed-btn ${speed === s ? "on" : ""}`}
                  aria-pressed={speed === s}
                  onClick={() => setSpeed(s)}>{s}&times;</button>
        ))}
      </div>
    </div>
  );
}

/**
 * A KNOWLEDGE CHECK ON A LESSON SCREEN.
 *
 * THIS IS FORMATIVE AND IT BLOCKS NOTHING. A wrong answer shows the feedback and the student
 * carries on; the 50-item final exam is where gating happens. Nothing here touches the
 * completion endpoint, the item bank, RECALL_CAP or PGA credit — verified separately by
 * tests/kc_isolation_check.py, which found 0 check_* fields in edu_items.
 *
 * THE INCORRECT FEEDBACK IS THE TEACHING, not a buzzer. It is written to correct one specific
 * misconception, so it is shown in full and given the same weight as the correct answer's —
 * the wrong branch is the one the student actually needs to read.
 *
 * THE ANSWER IS NOT REVEALED UNTIL THEY CHOOSE: no option carries a visual tell, and the
 * correct/incorrect styling is applied only after `chosen` is set. The answer does travel in
 * the lesson payload, so a student with devtools could find it — acceptable here because this
 * check gates nothing and awards nothing, and the real exam is scored server-side from a
 * different collection entirely. Said out loud rather than left as an assumption.
 *
 * Answers are reported UPWARD, and ride along with the completion the student was going to
 * send anyway. No second progress store — the legacy 36-module system is the cautionary tale.
 */
function LessonCheck({ seg, index, onAnswer }) {
  const [chosen, setChosen] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const options = seg.check_options || [];
  if (!seg.check_stem || options.length === 0) return null;

  const isRight = chosen !== null && chosen === seg.check_answer;

  const pick = (opt) => {
    if (chosen === opt) return;
    const n = attempts + 1;
    setChosen(opt);
    setAttempts(n);
    if (onAnswer) {
      onAnswer({ screen_index: index, chosen: opt,
                 correct: opt === seg.check_answer, attempts: n });
    }
  };

  return (
    <section className="edu-check" data-testid="edu-check">
      <div className="edu-eyebrow">Knowledge check</div>
      {/* markdown-aware: stems and options carry emphasis. renderInline returns React
          elements, so this cannot inject markup. */}
      <p className="edu-check-stem">{renderInline(seg.check_stem, "stem")}</p>
      <div className="edu-check-options" role="group" aria-label="Answer options">
        {options.map((opt, oi) => {
          const picked = chosen === opt;
          const state = chosen === null ? "" : picked
            ? (isRight ? " correct" : " wrong")
            : (opt === seg.check_answer && !isRight ? " reveal" : "");
          return (
            <button key={oi} type="button"
                    className={`edu-check-option${state}`}
                    aria-pressed={picked}
                    onClick={() => pick(opt)}>
              {renderInline(opt, `opt-${oi}`)}
            </button>
          );
        })}
      </div>
      {chosen !== null && (
        <div className={`edu-check-feedback ${isRight ? "ok" : "no"}`}
             role="status" data-testid="edu-check-feedback"
             /* MEASURED at 375px: a scenario stem runs ~90 words, so the options plus the
                feedback do not fit one phone screen with the question. `block: "nearest"`
                scrolls the MINIMUM needed, which keeps the chosen option and the feedback
                together rather than jumping the question off the top. */
             ref={(el) => el && el.scrollIntoView({ block: "nearest",
                                                    behavior: "smooth" })}>
          <b>{isRight ? "Correct." : "Not quite."}</b>{" "}
          {renderInline(isRight ? seg.check_feedback_correct : seg.check_feedback_incorrect, "fb")}
          {!isRight && (
            <p className="edu-check-carryon">
              You can carry on — this check is here to teach, not to hold you back.
            </p>
          )}
        </div>
      )}
    </section>
  );
}

/**
 * A REFERENCE CHART ON A LESSON SCREEN — currently the Fit Chart.
 *
 * NOT AN IMAGE, DELIBERATELY. A 5×4 grid of text as a PNG is unreadable at 375px, which is the
 * exact failure the mobile work just fixed, and as hand-built SVG it would be a second copy of
 * the manual that drifts from it. It renders from DATA: one record, five outcomes, ten
 * components, three influence levels.
 *
 * LIE ANGLE APPEARS TWICE IN THE CENTEREDNESS ROW and that is correct — the row measures
 * centeredness AND concentration of hit, and lie angle affects them by different amounts.
 * Nothing here de-duplicates by component name; the entries arrive as triples and are grouped,
 * never keyed. test_fit_chart.py fails against a model that would lose it.
 *
 * Real table on desktop, tap-to-expand outcome rows on a phone — a fitter opening this mid-
 * fitting is on a phone, and that is the case that has to work.
 */
/**
 * A FLEX BAND. The colour is printed on the shaft and a fitter genuinely uses it — but roughly
 * one man in twelve cannot reliably separate the green from the brown, so the swatch NEVER
 * appears without its name. The label is not optional here and it is not optional in the data:
 * `parse_chips` refuses a cell that carries a colour and nothing else, so this component
 * cannot be handed one.
 */
function FlexChip({ chip }) {
  if (!chip || !chip.label) return null;          // no name, no swatch. Deliberate.
  return (
    <span className="edu-flex-chip" data-testid="edu-flex-chip">
      <i className="edu-flex-dot" style={{ background: chip.hex }} aria-hidden="true" />
      <span className="edu-flex-name">{chip.label}</span>
      <span className="edu-flex-colour">({chip.colour})</span>
    </span>
  );
}

function GridCell({ cell }) {
  if (cell && cell.chips) {
    return (
      <span className="edu-flex-chips">
        {cell.chips.map((c, i) => <FlexChip key={i} chip={c} />)}
      </span>
    );
  }
  return <>{(cell && cell.text) || "—"}</>;
}

/**
 * A REFERENCE GRID — rows a fitter SCANS.
 *
 * Desktop gets a real table so it can be read across and copied out. Below 1081px a twelve-row,
 * six-column table cannot be read at all, so each row becomes a card with every field labelled
 * — the same tap-to-expand idea the Fit Chart uses, applied to a wider grid.
 *
 * The screen shows this. The NARRATION says `spoken_form`, which is a different sentence
 * entirely: nobody wants twelve rows of shaft data read to them.
 */
function ReferenceGrid({ chart }) {
  const [open, setOpen] = useState(null);
  const cols = chart.columns || [];
  const emphasis = chart.emphasis_column;

  return (
    <section className="edu-chart edu-grid-chart" data-testid="edu-reference-grid">
      <div className="edu-eyebrow">{chart.title}</div>
      {chart.subtitle && <p className="edu-chart-sub">{chart.subtitle}</p>}

      <div className="edu-chart-table-wrap">
        <table className="edu-chart-table edu-grid-table">
          <thead>
            <tr>{cols.map((c) => (
              <th key={c.key} scope="col"
                  className={c.key === emphasis ? "emphasis" : undefined}>{c.label}</th>
            ))}</tr>
          </thead>
          {(chart.groups || []).map((g, gi) => (
            <tbody key={gi}>
              {g.name && (
                <tr className="edu-grid-group">
                  <th scope="colgroup" colSpan={cols.length}>{g.name}</th>
                </tr>
              )}
              {g.rows.map((row, ri) => (
                <tr key={ri}>
                  {row.map((cell, ci) => {
                    const isEmph = cols[ci] && cols[ci].key === emphasis;
                    const Tag = ci === 0 ? "th" : "td";
                    return (
                      <Tag key={ci} scope={ci === 0 ? "row" : undefined}
                           className={isEmph ? "emphasis" : undefined}>
                        <GridCell cell={cell} />
                      </Tag>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          ))}
        </table>
      </div>

      {/* MOBILE: one card per row, every field labelled with its column.

          NARROW GRIDS DO NOT COLLAPSE. Hiding the single value of a two-column label/value
          table behind a tap makes the reader work for nothing — measured: the weights grid
          rendered "Head, Shaft, Grip, Total" with every number a tap away. Expand-to-reveal
          earns its place on the six-column shaft table and nowhere else. */}
      <div className="edu-grid-cards">
        {(chart.groups || []).map((g, gi) => (
          <div key={gi} className="edu-grid-group-block">
            {g.name && <div className="edu-grid-group-name">{g.name}</div>}
            {cols.length <= 3 ? (
              <dl className="edu-grid-flat">
                {g.rows.map((row, ri) => (
                  <div key={ri}>
                    <dt><GridCell cell={row[0]} /></dt>
                    {row.slice(1).map((cell, ci) => (
                      <dd key={ci}><GridCell cell={cell} /></dd>
                    ))}
                  </div>
                ))}
              </dl>
            ) : g.rows.map((row, ri) => {
              const k = `${gi}:${ri}`;
              const isOpen = open === k;
              return (
                <div key={ri} className={`edu-chart-row${isOpen ? " open" : ""}`}>
                  <button type="button" className="edu-chart-toggle"
                          aria-expanded={isOpen}
                          onClick={() => setOpen(isOpen ? null : k)}>
                    <span className="edu-grid-rowname"><GridCell cell={row[0]} /></span>
                    <span aria-hidden="true">{isOpen ? "−" : "+"}</span>
                  </button>
                  {isOpen && (
                    <dl className="edu-grid-detail">
                      {row.slice(1).map((cell, ci) => {
                        const col = cols[ci + 1] || {};
                        return (
                          <div key={ci}
                               className={col.key === emphasis ? "emphasis" : undefined}>
                            <dt>{col.label}</dt>
                            <dd><GridCell cell={cell} /></dd>
                          </div>
                        );
                      })}
                    </dl>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {(chart.colour_key || []).length > 0 && (
        <div className="edu-colour-key" data-testid="edu-colour-key">
          <div className="edu-eyebrow">Flex bands, as printed on the shaft</div>
          <ul>
            {chart.colour_key.map((c) => (
              <li key={c.colour}>
                <i className="edu-flex-dot" style={{ background: c.hex }} aria-hidden="true" />
                <b>{c.colour}</b> — {c.label}
              </li>
            ))}
          </ul>
        </div>
      )}

      {(chart.replacements || []).length > 0 && (
        <p className="edu-chart-note">
          On an older order form:{" "}
          {chart.replacements.map((r, i) => (
            <span key={r.from}>{i > 0 ? ", " : ""}<b>{r.from}</b> is now <b>{r.to}</b></span>
          ))}.
        </p>
      )}

      {chart.note && <p className="edu-chart-note">{chart.note}</p>}
      {chart.source && <p className="edu-chart-source">{chart.source}</p>}
    </section>
  );
}

function ReferenceChart({ chart }) {
  const [open, setOpen] = useState(null);
  // TWO KINDS, ONE MECHANISM. Absence means matrix, so every Fit Chart document already stored
  // in Atlas renders exactly as it did before reference grids existed.
  if (chart && (chart.kind === "grid" || chart.groups)) return <ReferenceGrid chart={chart} />;
  if (!chart || !chart.entries) return null;

  const label = Object.fromEntries((chart.components || []).map((c) => [c.key, c.label]));
  const levels = (chart.influences || []).map((i) => i.key);
  const cell = (outcome, level) => chart.entries
    .filter((e) => e.outcome === outcome && e.influence === level)
    .map((e) => label[e.component] || e.component);

  return (
    <section className="edu-chart" data-testid="edu-chart">
      <div className="edu-eyebrow">{chart.title}</div>
      {chart.subtitle && <p className="edu-chart-sub">{chart.subtitle}</p>}

      {/* DESKTOP: a real table, so it can be read across and copied out. */}
      <div className="edu-chart-table-wrap">
        <table className="edu-chart-table">
          <thead>
            <tr>
              <th scope="col">Outcome</th>
              {(chart.influences || []).map((i) => (
                <th key={i.key} scope="col">{i.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(chart.outcomes || []).map((o) => (
              <tr key={o.key}>
                <th scope="row">{o.label}</th>
                {levels.map((lv) => (
                  <td key={lv}>{cell(o.key, lv).join(" · ") || "—"}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* MOBILE: tap an outcome, see it ranked. */}
      <div className="edu-chart-rows">
        {(chart.outcomes || []).map((o) => {
          const isOpen = open === o.key;
          return (
            <div key={o.key} className={`edu-chart-row${isOpen ? " open" : ""}`}>
              <button type="button" className="edu-chart-toggle"
                      aria-expanded={isOpen}
                      onClick={() => setOpen(isOpen ? null : o.key)}>
                <span>{o.label}</span>
                <span aria-hidden="true">{isOpen ? "−" : "+"}</span>
              </button>
              {isOpen && (
                <div className="edu-chart-detail">
                  {(chart.influences || []).map((i) => {
                    const items = cell(o.key, i.key);
                    if (!items.length) return null;
                    return (
                      <div key={i.key} className={`edu-chart-band ${i.key}`}>
                        <div className="edu-chart-band-label">{i.label}</div>
                        <ul>{items.map((n, ni) => <li key={ni}>{n}</li>)}</ul>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {chart.note && <p className="edu-chart-note">{chart.note}</p>}
      {chart.source && <p className="edu-chart-source">{chart.source}</p>}
    </section>
  );
}

/**
 * DIAGNOSE A LIE MARK — the Appendix B practice.
 *
 * COMMIT BEFORE REVEAL. That is the whole pedagogical point: a student who can see the answer
 * while still deciding has practised nothing. Both axes must be set before Commit does
 * anything, and nothing on screen hints at the answer until then.
 *
 * TWO AXES, NOT THREE-WAY. Face perpendicular / open / closed × lie accurate / too upright /
 * too flat. The manual holds swingpath constant, so it is not an axis — which is why the
 * 27-case generated set that varied it is voided.
 *
 * FOUR CASES ARE TRAPS AND THE REVEAL SAYS SO. Getting IV, V, VII or IX wrong is the lesson
 * the case exists to teach, so a wrong answer on those shows the manual's own warning rather
 * than a buzzer. Nothing here gates progression, touches the item bank, or awards credit.
 *
 * THE FIGURE IS THE CONTENT, so it is tappable to enlarge — at 375px a four-panel illustration
 * is unreadable at column width.
 */
function LessonCase({ seg, index, onAnswer }) {
  const c = seg.case;
  const [face, setFace] = useState("");
  const [lie, setLie] = useState("");
  const [committed, setCommitted] = useState(false);
  const [zoom, setZoom] = useState(false);
  if (!c) return null;

  const bothRight = face === c.answer_face && lie === c.answer_lie;
  const isTrap = Boolean(c.reveal && c.reveal.trap);
  const label = (opts, key) => (opts.find((o) => o.key === key) || {}).label || key;

  const commit = () => {
    if (!face || !lie || committed) return;
    setCommitted(true);
    if (onAnswer) {
      onAnswer({ screen_index: index, chosen: face + "/" + lie,
                 correct: face === c.answer_face && lie === c.answer_lie, attempts: 1 });
    }
  };

  return (
    <section className="edu-case" data-testid="edu-case">
      <div className="edu-eyebrow">Read the mark — case {c.case}</div>

      <button type="button" className="edu-case-figure" onClick={() => setZoom(!zoom)}
              aria-label={zoom ? "Shrink the figure" : "Enlarge the figure"}>
        <img src={`${API_URL}/api/education/media/${c.figure_slug}`}
             crossOrigin="use-credentials" loading="lazy"
             className={zoom ? "zoomed" : ""}
             alt="Lie board mark: face angle, impact, sole and heel-on views" />
        <span className="edu-case-zoomhint">{zoom ? "Tap to shrink" : "Tap to enlarge"}</span>
      </button>

      {!committed ? (
        <div className="edu-case-form">
          <p className="edu-case-ask">
            Read the mark. What is the <b>face angle</b>, and what is the <b>lie</b>?
          </p>
          <label className="edu-case-field">
            <span>Face angle</span>
            <select value={face} onChange={(e) => setFace(e.target.value)}
                    data-testid="edu-case-face">
              <option value="">Choose…</option>
              {c.face_options.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
            </select>
          </label>
          <label className="edu-case-field">
            <span>Lie angle</span>
            <select value={lie} onChange={(e) => setLie(e.target.value)}
                    data-testid="edu-case-lie">
              <option value="">Choose…</option>
              {c.lie_options.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
            </select>
          </label>
          <button type="button" className="edu-btn edu-case-commit"
                  disabled={!face || !lie} onClick={commit}
                  data-testid="edu-case-commit">
            Commit
          </button>
          <p className="edu-case-note">
            Ungraded. Commit before you see the answer — that is the point.
          </p>
        </div>
      ) : (
        <div className={`edu-case-reveal ${bothRight ? "ok" : "no"}`}
             data-testid="edu-case-reveal">
          <div className="edu-case-verdict">
            <b>{bothRight ? "Both right." : "Read it again."}</b>{" "}
            You said {label(c.face_options, face)} · {label(c.lie_options, lie)}.
            {!bothRight && (
              <> The manual reads it as <b>{label(c.face_options, c.answer_face)}
                {" · "}{label(c.lie_options, c.answer_lie)}</b>.</>
            )}
          </div>

          {!bothRight && isTrap && (
            <div className="edu-case-trap" data-testid="edu-case-trap">
              <div className="edu-eyebrow">This case is a known trap</div>
              <p>{c.reveal.trap}</p>
            </div>
          )}

          <dl className="edu-case-evidence">
            <dt>Mark position</dt><dd>{c.reveal.position}</dd>
            <dt>Mark shape</dt><dd>{c.reveal.shape}</dd>
            <dt>Ball flight</dt><dd>{c.reveal.flight}</dd>
          </dl>
          <p className="edu-case-note">
            Ungraded practice — carry on whichever way you read it.
          </p>
        </div>
      )}
    </section>
  );
}

function LessonVideo({ url, caption, onEnded, transcript }) {
  if (!url) return null;
  // THE VIDEO LIVES ON THE API ORIGIN; THE FIGURES DO NOT. The registry stores a path
  // ("/api/education/media/loft.mp4") and is right to - it must not know deployment origins.
  // But a bare path resolves against the FRONTEND origin, where /api/* falls through to the
  // SPA catch-all and returns index.html with a 200. A <video> handed HTML plays nothing and
  // says nothing, so this failed silently on every one of the 14 films. Only /api/ paths get
  // the backend origin - /figures/* really is served by the frontend.
  const src = url.startsWith("/api/") ? `${API_URL}${url}` : url;
  return (
    <figure className="edu-plate edu-video" data-testid="edu-video">
      <video controls preload="metadata" playsInline
             /* no autoPlay. onEnded only tells the screen to show its Back / Next under the film
                (feedback #27); it never marks progress - see above */
             onEnded={onEnded}
             /* use-credentials: this request is cross-origin and the route is gated on the
                auth COOKIE. Without it the element sends no credentials and gets a 401. */
             crossOrigin="use-credentials"
             style={{ width: "100%", height: "auto", display: "block", background: "#000" }}>
        <source src={src} type="video/mp4" />
        Your browser cannot play this video.
      </video>
      {caption && <figcaption>{caption}</figcaption>}
      {/* READ ALONG WITH THE FILM (feedback #2, 2026-10-16). Shown only when the screen carries the film's own
          transcript (`video_transcript`): the words spoken in the film, taken from its audio and checked before they
          go in. A screen's narration needs none of this -- its text is already on the screen -- and nothing is shown
          for a film with no transcript rather than a placeholder. */}
      {String(transcript || "").trim() && (
        <details className="edu-film-transcript" data-testid="edu-film-transcript">
          <summary>Read along</summary>
          <div className="edu-film-transcript-body">{renderBlocks(String(transcript).trim())}</div>
        </details>
      )}
    </figure>
  );
}

function LessonText({ text, pending }) {
  const body = String(text || "").trim();
  if (!body) {
    return (
      <p className="edu-lesson-p muted">This lesson's text is being written.</p>
    );
  }
  const paras = body.split(/\n\n+/).map((p) => p.trim()).filter(Boolean);
  const isSources = (p) => /^sources:/i.test(p);
  const isPending = (p) => /narration for this lesson is in production/i.test(p);
  // THE TEXT IS MARKDOWN AND WAS BEING SHOWN AS SOURCE. Every paragraph used to render as a
  // text node, so a student read literal `**` on nearly every screen of the course — 1,423 bold
  // spans across 141 screens in 11 chapters, measured 2026-08-31. renderBlocks returns React
  // ELEMENTS, never markup, so nothing here can inject HTML; see lib/markdown.js.
  const visible = paras.filter((p) => !isSources(p) && !isPending(p)).join("\n\n");
  return (
    <>
      {renderBlocks(visible)}
      {/* THE SOURCE LINE IS NOT RENDERED TO THE STUDENT, and the decision is deliberate.
          It read `archive/Cse 1, Chp 1, Sec 3 (company history); ...` - an internal file path
          a student cannot look up, with "Cse" a mangled "Course". Making it human-readable
          would still hand them an internal course reference; the provenance belongs to
          authoring, where it is kept on each segment's manual_source and in the item banks'
          citations. The lesson text IS the narration script, so this line would also have been
          READ ALOUD by Randy - which is the reason it cannot merely be styled smaller. It is
          stripped from the script server-side as well; this is the second line of defence. */}
      {(pending || paras.some(isPending)) && (
        <p className="edu-pending" data-testid="edu-narration-pending">
          Narration is in production — the text above is the lesson's content.
        </p>
      )}
    </>
  );
}

// An image item must never be a dead end. With no figure the student still gets the
// description as visible copy, and the question stays answerable.
function ItemFigure({ svg, alt }) {
  const clean = sanitiseSvg(svg);
  const label = alt || "Figure for this question";
  if (!clean) {
    if (!alt) return null;                       // not an image item at all
    return (
      <div className="edu-figure missing" role="group" aria-label="Figure unavailable">
        <AlertTriangle size={14} aria-hidden="true" />
        <div>
          <b>This figure could not be loaded.</b>
          <p className="edu-p muted" style={{ marginTop: 4 }}>{label}</p>
          <p className="edu-p faint" style={{ marginTop: 4, fontSize: 12 }}>
            Answer from the description above, and tell Henry-Griffitts the diagram is missing.
          </p>
        </div>
      </div>
    );
  }
  return (
    <div className="edu-figure">
      {/* Fixed-size container: the SVG scales into it instead of dictating layout. The
          description is the accessible name — the graphic is presentational, so a screen
          reader reads the sentence rather than a pile of unlabelled shapes. */}
      <div className="edu-figure-svg" role="img" aria-label={label}
        data-testid="edu-item-image"
        dangerouslySetInnerHTML={{ __html: clean }} />
    </div>
  );
}

// ONLY SCREENS THAT EXIST. "My Progress", "Certification" and "PGA / LPGA Credits" were here and
// all three opened the same card saying they would be "built in Phase 2" (Wave 2 placeholder
// sweep, 2026-09-19). A nav item that leads nowhere is a control that looks real and does
// nothing. They come back when Wave 3 builds them.
// WAVE 3 BRINGS ONE OF THE THREE BACK. "My Record" is the transcript: what you have completed,
// when, how each quiz scored, and the hours. It is built on GET /transcript, which serves only
// the caller's own data. The other two (certification ladder, PGA/LPGA credits) stay absent --
// both are still waiting on a decision about what the thing IS, and a nav item that leads to a
// screen nobody has defined is the placeholder this sweep removed.
const RAIL = [
  { id: "home", label: "My Learning", Icon: Home },
  { id: "curriculum", label: "Curriculum", Icon: BookOpen },
  { id: "record", label: "My Record", Icon: ClipboardList },
];

/**
 * WHAT THE RAIL CALLS THE PERSON LOOKING AT IT.
 *
 * This was the literal string "Fitter", which an education-only account is not - it is the
 * first thing a certification student is told about themselves and it was wrong. Deriving it
 * from the role means it cannot drift from what the server actually granted.
 *
 * ABSENCE MEANS FITTER, matching education_access.role_of on the server. A missing or
 * misspelled role must not relabel an existing fitter.
 *
 * PROPOSED WORDING, NOT SETTLED: "Student" is Randall's own word for the learner - he
 * corrected "customer" to "student" when chapter 12 and 17 were written - so it is the
 * candidate rather than a decision. Changing it is this one map.
 */
export const ROLE_LABELS = {
  fitter: "Fitter",
  education_only: "Student",
};

export function roleLabel(user) {
  return ROLE_LABELS[user?.role] || ROLE_LABELS.fitter;
}

function Rail({ screen, go, user, onReference }) {
  const initials = (user?.name || user?.email || "")
    .split(/[\s@.]/).filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase()).join("");
  return (
    <aside className="edu-rail">
      <div className="edu-brand">
        {/* THE H-G TREE. The previous mark was a generic narrow CONIFER drawn as an inline
            path - pointed, symmetrical, no canopy detail. The Henry-Griffitts tree is broad and
            rounded with a slim trunk and white negative-space gaps in the canopy, and it is a
            brand asset rather than an icon to be approximated. Randall supplied the file; it is
            served from public/hg-tree.png rather than re-traced, because a hand-traced
            approximation of a logo is how you end up with a third wrong tree. */}
        <img src="/hg-tree.png" width="26" height="26" alt="" aria-hidden="true"
             style={{ objectFit: "contain" }} />
        <div className="edu-brand-name"><b>Henry-Griffitts</b><i>Education</i></div>
      </div>
      <nav>
        {RAIL.map(({ id, label, Icon }) => (
          <button key={id} onClick={() => go(id)}
            className={`edu-nav-item ${screen === id ? "on" : ""}`}
            data-testid={`edu-nav-${id}`}>
            <Icon size={16} /> {label}
          </button>
        ))}
      </nav>
      {/* ONE PLACE, EVERY SCREEN: the rail is on the lesson, the quiz and the final exam alike. */}
      {onReference && (
        <button className="edu-ref-open" onClick={onReference} data-testid="edu-reference-open"
                title="Reference (R)">
          <Library size={16} /> Reference
        </button>
      )}
      <div className="edu-rail-foot">
        <div className="edu-who">
          <div className="edu-avatar">{initials || "HG"}</div>
          <div>
            <div className="edu-who-n">
              {user?.name || user?.email || roleLabel(user)}
            </div>
            <div className="edu-who-r" data-testid="edu-role-badge">{roleLabel(user)}</div>
          </div>
        </div>
      </div>
    </aside>
  );
}

function Ring({ percent }) {
  const r = 45, c = 2 * Math.PI * r;
  return (
    <div className="edu-ring">
      <svg width="104" height="104">
        <circle cx="52" cy="52" r={r} stroke="#1A1A1A" strokeWidth="8" fill="none" />
        <circle cx="52" cy="52" r={r} stroke="var(--hg-green)" strokeWidth="8" fill="none"
          strokeLinecap="round" strokeDasharray={c}
          strokeDashoffset={c - (c * (percent || 0)) / 100}
          transform="rotate(-90 52 52)" />
      </svg>
      <div className="edu-ring-v"><b className="num">{percent || 0}%</b><i>Complete</i></div>
    </div>
  );
}

// ------------------------------------------------------------- My Learning

/**
 * THE ONE PLACE THAT DECIDES WHAT A TRACK'S PROGRESS MEANS.
 *
 * THIS IS THE THIRD TIME THIS BUG HAS SHIPPED on this project, so it gets an owner rather than
 * a third patched call site. The previous two: `allComplete` derived from 36 legacy modules and
 * read true when there were none, and `chapter_lessons_complete` counting voided rows. Here,
 * My Learning branched on `resume ? ... : "Track complete"` — and `resume` is null BOTH when a
 * student has finished everything AND when they have never started. A student at 0% was told
 * "Every lesson in Track A is marked complete."
 *
 * ZERO OF ZERO IS NOT DONE. Four states, and "complete" requires evidence of completion rather
 * than an absence of anything to resume:
 *   not_enrolled  — no enrolment. The only state that can offer an enrol action.
 *   not_started   — enrolled, nothing completed yet.
 *   in_progress   — something completed, something left; `resume` points at the next lesson.
 *   complete      — chapters_complete > 0 AND every chapter done. Both halves are required.
 */
export function trackProgressState(track, data) {
  if (!track || track.enrolled === false || !data?.enrollment) return "not_enrolled";
  const done = Number(track.chapters_complete ?? 0);
  const total = Number(track.chapters ?? 0);
  if (total > 0 && done >= total) return "complete";
  if (data?.resume) return "in_progress";
  return "not_started";
}

/** Does this track object actually carry progress figures? A card must never render a
 *  template with the numbers filed off — "of chapters" and a bare "%" read as data. */
export function hasProgressFigures(track) {
  return Boolean(track) && typeof track.chapters === "number"
    && typeof track.percent === "number";
}


// WHICH BODY A FITTER BELONGS TO (Randall, 2026-10-05). Asked here, in the app, because outbound mail is off. It
// blocks nothing: a fitter who has not answered still learns, completes and is certified. His completion record is
// addressed to the body or bodies he names; with no answer it waits, and Henry-Griffitts cannot authorise it.
export function MembershipCard({ testid = "edu-membership" }) {
  const [m, setM] = useState(null);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({ pga: false, pga_number: "", lpga: false, lpga_number: "", neither: false });
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    let live = true;
    api("/me/memberships").then((d) => { if (live) { setM(d); setForm(d); setEdit(!d.declared); } })
      .catch(() => { if (live) setM(null); });
    return () => { live = false; };
  }, []);
  if (!m) return null;
  const save = async () => {
    setErr(""); setSaving(true);
    try {
      const d = await api("/me/memberships", { method: "PUT", body: JSON.stringify(form) });
      setM(d); setForm(d); setEdit(false);
    } catch (e) { setErr(e.message); } finally { setSaving(false); }
  };
  const said = m.neither ? "You have said you are a member of neither the PGA nor the LPGA."
    : [m.pga && `PGA${m.pga_number ? ` (member number ${m.pga_number})` : " (no member number given)"}`,
       m.lpga && `LPGA${m.lpga_number ? ` (member number ${m.lpga_number})` : " (no member number given)"}`]
      .filter(Boolean).join(" and ");
  return (
    <div className="edu-card" data-testid={testid} style={{ marginTop: 14 }}>
      <div className="edu-h-card">Your PGA or LPGA membership</div>
      {!edit ? (
        <div className="edu-p">
          {m.neither ? said : `You are recorded as a member of the ${said}.`}{" "}
          <button className="edu-btn ghost" onClick={() => setEdit(true)} data-testid={`${testid}-change`}>Change</button>
        </div>
      ) : (
        <>
          <p className="edu-p muted">
            If you are a member of the PGA or the LPGA, tick it and give your member number, so your completion record
            is addressed to the body you belong to. If you belong to neither, say so. It makes no difference to your
            course or your certificate.
          </p>
          {[["pga", "PGA"], ["lpga", "LPGA"]].map(([k, label]) => (
            <div key={k} style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 8, flexWrap: "wrap" }}>
              <label><input type="checkbox" checked={!!form[k]} data-testid={`${testid}-${k}`}
                onChange={(e) => setForm({ ...form, [k]: e.target.checked, neither: false })} /> I am a member of the {label}</label>
              {form[k] && (
                <input type="text" value={form[`${k}_number`] || ""} maxLength={32} placeholder={`${label} member number`}
                  aria-label={`${label} member number`} data-testid={`${testid}-${k}-number`}
                  onChange={(e) => setForm({ ...form, [`${k}_number`]: e.target.value })} />
              )}
            </div>
          ))}
          <div style={{ marginTop: 8 }}>
            <label><input type="checkbox" checked={!!form.neither} data-testid={`${testid}-neither`}
              onChange={(e) => setForm({ ...form, neither: e.target.checked, pga: false, lpga: false })} /> I am a member of neither</label>
          </div>
          {err && <p className="edu-p" data-testid={`${testid}-error`}>{err}</p>}
          <div style={{ marginTop: 12 }}>
            <button className="edu-btn" onClick={save} disabled={saving || !(form.pga || form.lpga || form.neither)}
              data-testid={`${testid}-save`}>{saving ? "Saving…" : "Save"}</button>
          </div>
        </>
      )}
    </div>
  );
}

function MyLearning({ data, go, openLesson, onEnroll, enrolling }) {
  const trackA = (data.tracks || []).find((t) => t.key === "A");
  const resume = data.resume;
  const state = trackProgressState(trackA, data);
  return (
    <>
      <div className="edu-topbar">
        <h3 className="edu-h3 display">My Learning</h3>
        <div className="edu-crumbs">
          Hours logged <b className="num">{data.hours_logged_display}</b>
        </div>
      </div>
      <div className="edu-body">
        <div className="edu-card edu-hero">
          <Ring percent={trackA?.percent || 0} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="edu-eyebrow">{trackA?.subtitle || "Track A · Certified Fitter"}</div>
            {state === "in_progress" && resume ? (
              <>
                <div className="edu-h-card" style={{ fontSize: 19 }}>
                  Chapter {resume.chapter.number} — {resume.chapter.title}
                </div>
                <div className="edu-p muted">
                  Lesson {resume.lesson.number} · {resume.lesson.title}
                </div>
                <div style={{ display: "flex", gap: 9, marginTop: 14 }}>
                  <button className="edu-btn" onClick={() => openLesson(resume.lesson.lesson_id)}
                    data-testid="edu-resume">
                    <Play size={14} /> Resume lesson
                  </button>
                  <button className="edu-btn ghost" onClick={() => go("curriculum")}>
                    View curriculum
                  </button>
                </div>
              </>
            ) : state === "not_enrolled" ? (
              <>
                {/* P0: THERE WAS NO WAY TO START. A student with a correct account on a
                    published course had no button, link or action that enrolled them. The
                    journey harness enrolled by calling the API, so the path a human takes was
                    never exercised by anything — and did not exist. */}
                <div className="edu-h-card" style={{ fontSize: 19 }}>Start the course</div>
                <div className="edu-p muted">
                  {trackA?.locked_reason
                    || "Enroll to open the chapter outline and begin chapter 1."}
                </div>
                <div style={{ display: "flex", gap: 9, marginTop: 14 }}>
                  <button className="edu-btn" onClick={onEnroll} disabled={enrolling}
                    data-testid="edu-enroll">
                    {enrolling ? <Loader2 size={14} className="spin" /> : <Play size={14} />}
                    {" "}{enrolling ? "Enrolling…" : "Enroll and begin"}
                  </button>
                </div>
              </>
            ) : state === "not_started" ? (
              <>
                <div className="edu-h-card" style={{ fontSize: 19 }}>Ready to begin</div>
                <div className="edu-p muted">
                  You are enrolled. Nothing is complete yet — open the curriculum to start
                  chapter 1.
                </div>
                <div style={{ display: "flex", gap: 9, marginTop: 14 }}>
                  <button className="edu-btn" onClick={() => go("curriculum")}
                    data-testid="edu-begin">
                    <Play size={14} /> Begin chapter 1
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="edu-h-card" style={{ fontSize: 19 }}>Track complete</div>
                <div className="edu-p muted">Every lesson in Track A is marked complete.</div>
              </>
            )}
          </div>
          {/* P2 (2026-08-11): this card used to read "Hours logged 42:00 / PGA REPORTABLE" beside
              a 0% ring — two different measures presented as one. The ring is CHAPTER progress
              from completions; the hours come from the CREDIT LEDGER. They can legitimately
              disagree: voiding a credit row removes the hours and leaves the completion, which is
              exactly what happened to the verification data. So both are labelled, and a
              disagreement is explained rather than left to look like a bug. */}
          <div className="edu-hero-hours">
            <div className="edu-eyebrow">PGA-reportable hours</div>
            <div className="num edu-big">{data.hours_logged_display}</div>
            {/* Size lives in CSS, not here. An inline style beats every stylesheet rule,
                so the mobile type pass could not raise this one and the audit kept reporting
                it at 11px after two rounds of "fixes". */}
            <div className="edu-faint">
              of {trackA?.runtime || "—"} course total
            </div>
            {/* Only claim a percentage when there IS one. `trackA?.percent || 0` printed
                "0% of chapters" to a student with no enrolment, which states a measurement
                that has not been taken. */}
            {typeof data.completions_count === "number" && hasProgressFigures(trackA) && (
              <div className="edu-faint" style={{ marginTop: 6 }}>
                {data.completions_count} lesson{data.completions_count === 1 ? "" : "s"} completed
                {" · "}{trackA.percent}% of chapters
              </div>
            )}
            <span className="edu-pill blue" style={{ marginTop: 10 }}>From the credit ledger</span>
            {data.completions_count > 0 && data.hours_logged_display === "0:00" && (
              <div className="edu-pending" style={{ marginTop: 8 }}>
                Lessons are complete but no hours are credited — the credit rows for these
                completions have been voided.
              </div>
            )}
          </div>
        </div>
        <MembershipCard />

        <div className="edu-grid g3" style={{ marginTop: 14 }}>
          {(data.tracks || []).map((t) => (
            <div className="edu-card" key={t.track_id}>
              <div className="edu-eyebrow">{t.subtitle}</div>
              <div className="edu-h-card">{t.title}</div>
              {/* A CARD WITH NO DATA MUST NOT RENDER AS THOUGH IT HAS DATA. Before this, a
                  non-enrolled track produced "chapters ·", " of  chapters" and a bare "%" —
                  labels with the numbers filed off, which reads as broken rather than empty.
                  The API genuinely omits these fields until enrolment; the card now says so. */}
              {hasProgressFigures(t) ? (
                <>
                  <p className="edu-p muted" style={{ marginBottom: 12 }}>
                    {t.chapters} chapters · {t.runtime}
                  </p>
                  <div className={`edu-bar ${t.key === "A" ? "" : "gold"}`}>
                    <i style={{ width: `${t.percent}%` }} />
                  </div>
                  <div className="edu-bar-legend">
                    <span>{t.chapters_complete} of {t.chapters} chapters</span>
                    <span>
                      {t.access === "locked_until_certified"
                        ? <span className="edu-pill">Locked</span>
                        : <span className="num">{t.percent}%</span>}
                    </span>
                  </div>
                </>
              ) : (
                <p className="edu-p muted" data-testid="edu-card-empty"
                   style={{ marginBottom: 4 }}>
                  {t.locked_reason || "Enroll to see the chapter outline."}
                </p>
              )}
            </div>
          ))}
        </div>

        {(data.recent || []).length > 0 && (
          <div className="edu-card" style={{ marginTop: 14 }}>
            <div className="edu-eyebrow">Recently completed</div>
            {data.recent.map((c) => (
              <div className="edu-chap done" key={c.completion_id}>
                <div className="edu-ix"><Check size={11} /></div>
                <div className="edu-chap-t">
                  {/* P2 (2026-08-11): three completed lessons in one chapter rendered as three
                      identical chapter names. The lesson is what was completed; the chapter is
                      context. */}
                  <b>{c.lesson_title || c.chapter_title}</b>
                  {/* ONE SPAN, NOT TWO. `.edu-chap-t b` is display:block but
                      `.edu-chap-t span` is not, so two sibling spans ran together with
                      nothing between them: `Ch. 1 · Values & History2026-08-25`. Composing
                      the line here rather than adding a CSS rule keeps the separator the
                      same " · " this component already uses, and keeps the date on the same
                      line instead of making every row of a three-row list two lines tall. */}
                  <span className="edu-faint">
                    {c.lesson_title
                      ? <>Ch. {c.chapter_number} · {c.chapter_title} · </>
                      : null}
                    {fmtDay(c.completed_at)}
                  </span>
                </div>
                <div className="edu-chap-meta">
                  <span className="edu-rt num">{c.runtime}</span>
                  <span className="edu-pill ok">Done</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

// -------------------------------------------------------------- Curriculum
function Curriculum({ trackId, openLesson, openQuiz }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  // ONE CHAPTER OPEN AT A TIME, by chapter_id. An accordion rather than a set of independent
  // toggles: seventeen chapters all expanded is the table of contents this list exists to
  // avoid being, and a fitter looking for the lie-angle screens wants one chapter's worth of
  // detail, not the whole course flattened.
  const [expanded, setExpanded] = useState(null);
  useEffect(() => {
    if (!trackId) return;
    api(`/tracks/${trackId}/curriculum`).then(setData).catch((e) => setErr(e.message));
  }, [trackId]);

  if (err) return <div className="edu-body edu-error">{err}</div>;
  if (!data) return <div className="edu-body edu-loading"><Loader2 className="spin" /></div>;

  // A chapter row opens the first lesson the student has not finished. The id comes from the
  // curriculum payload — the shell used to have no id at all here, and the click did nothing.
  const openChapter = (ch) => {
    if (ch.state === "locked") return;
    if (ch.is_final_exam) { openQuiz({ trackId, kind: "final_exam" }); return; }
    const lid = ch.next_lesson_id || ch.first_lesson_id;
    if (lid) { openLesson(lid, ch.chapter_id); return; }
    setErr(`Chapter ${ch.number} has no lessons imported yet — nothing to open.`);
  };

  return (
    <>
      <div className="edu-topbar">
        {/* FOUND BY THE FRESH-ACCOUNT ENROLMENT TEST, which is the whole reason it was
            written. The curriculum payload is fetched by track_id; immediately after enrolling,
            a render can reach here before `track` is populated, and `data.track.subtitle` threw.
            The ErrorBoundary caught it so it was never a white screen — but a student clicking
            "Enroll and begin" hit an error panel on the first thing they ever do. */}
        <h3 className="edu-h3 display">
          {data.track
            ? <>{data.track.subtitle} — {data.track.title}</>
            : "Curriculum"}
        </h3>
        <div className="edu-crumbs">
          {/* THE SERVER'S COUNT, not the array length. The array carries the Final Exam row,
              which renders as "★ Final Exam" rather than a numbered chapter — so counting the
              array said 18 while the list showed 17 and the dashboard card said 17. */}
          {data.chapter_count ?? (data.chapters || []).length} chapters
          {data.runtime ? <> · <b className="num">{data.runtime}</b> total runtime</> : null}
        </div>
      </div>
      <div className="edu-body scroll">
        {/* THE RECOMMENDED ORDER, STATED (Randall, 2026-09-28): the quiz gate no longer enforces
            it, so the course says it in words. The text is the server's (QUIZ_ORDER_NOTE). */}
        {data.order_note && (
          <p className="edu-p faint" data-testid="edu-order-note" style={{ margin: "0 0 10px" }}>
            {data.order_note}
          </p>
        )}
        <div className="edu-chip-row">
          <span className="edu-pill ok">{data.counts.done} complete</span>
          {/* A COUNT OF WHAT IS OPEN, not an award. Green, under the gold ruling. */}
          <span className="edu-pill ok">{data.counts.now} available</span>
          <span className="edu-pill">{data.counts.locked} locked</span>
          <span className="edu-pill blue">Runtime feeds PGA syllabus</span>
        </div>
        {data.chapters.map((ch) => {
          const locked = ch.state === "locked";
          const rows = ch.lesson_rows || [];
          // EXPANDABLE ONLY WHERE THERE IS SOMETHING TO SHOW AND A RIGHT TO SEE IT.
          // A locked chapter does not list its lessons: the ids would be inert (the lesson
          // route 403s them) but publishing a table of contents for a chapter a student
          // cannot open is a tease, not navigation. The Final Exam is one row and opens a
          // quiz, not a lesson.
          const canExpand = !locked && !ch.is_final_exam && rows.length > 0;
          const open = expanded === ch.chapter_id;
          return (
          <div key={ch.chapter_id} className="edu-chap-group">
          <div
            className={`edu-chap ${ch.state} ${ch.is_final_exam ? "final" : ""}`}
            data-testid={`edu-chapter-${ch.number}`}
            aria-disabled={locked}>
            <div className="edu-ix">
              {ch.state === "done" ? <Check size={11} />
                : ch.is_final_exam ? "★" : ch.number}
            </div>
            {/* THE ROW BODY STILL RESUMES, IN ONE CLICK. Expanding is an extra affordance
                beside it, not a toll gate in front of it — a student who just wants to carry
                on presses the same thing they always did and lands on the first unfinished
                lesson. A real <button> rather than role="button" on the wrapper, because the
                wrapper now contains a second control and nesting those is invalid. */}
            <button type="button" className="edu-chap-open"
                    disabled={locked}
                    data-testid={`edu-chapter-open-${ch.number}`}
                    onClick={() => openChapter(ch)}>
            <div className="edu-chap-t">
              <b>{ch.is_final_exam ? "Final Exam" : `${ch.number} · ${ch.title}`}</b>
              {/* WHAT PRECEDES A FINAL says it too (Randall 2026-10-02): open book, no clock, and where the book is. */}
              {ch.is_final_exam && (
                <span data-testid="edu-final-open-book">
                  Open book, with no time limit. The Reference panel (the Reference button in the menu, or the R key)
                  is open to you throughout.
                </span>
              )}
              {/* THE DESCRIPTION, NEVER `media_plan`. What rendered here was our internal
                  media schedule: chapter 10 told a fitter deciding whether to read it
                  "Product photography · reading", and chapter 13 "Flagship animation series
                  — one per spec error". Those are build-plan notes. `media_plan` stays in
                  the data — it is still the authoring record — and stops being shown.
                  NO FALLBACK. An empty description renders nothing at all: a chapter with
                  no description written yet should be silent, not reach for whatever other
                  string happens to be on the document. The remaster note keeps its own
                  line so it survives an empty description. */}
              {((ch.description || "").trim() || ch.needs_remaster > 0) && (
                <span>
                  {(ch.description || "").trim()}
                  {ch.needs_remaster > 0 && (
                    <em className="edu-remaster">
                      {(ch.description || "").trim() ? " · " : ""}
                      {ch.needs_remaster} being remastered
                    </em>
                  )}
                </span>
              )}
            </div>
            </button>
            <div className="edu-chap-meta">
              <span className="edu-rt num">{ch.runtime}</span>
              {ch.state === "now" && !ch.is_final_exam && <span className="edu-pill ok">Available</span>}
              {/* GOLD MEANS CERTIFICATION. This is the one pill in the chapter list that
                  earns it: passing this is the thing that certifies a fitter. */}
              {ch.is_final_exam && <span className="edu-pill gold">Certifies</span>}
              {canExpand && (
                <button type="button" className="edu-chap-expand"
                        aria-expanded={open}
                        aria-label={open ? `Hide chapter ${ch.number} lessons`
                                         : `Show chapter ${ch.number} lessons`}
                        data-testid={`edu-chapter-expand-${ch.number}`}
                        onClick={() => setExpanded(open ? null : ch.chapter_id)}>
                  {ch.lessons_complete}/{ch.lessons}
                  <ChevronRight size={13} className={open ? "rot" : ""} />
                </button>
              )}
            </div>
          </div>
          {open && (
            <div className="edu-lessons" data-testid={`edu-lessons-${ch.number}`}>
              {rows.map((ls) => (
                <button type="button" key={ls.lesson_id}
                        className={`edu-lesson-row${ls.is_next ? " next" : ""}`}
                        data-testid={`edu-lesson-${ch.number}-${ls.number}`}
                        onClick={() => openLesson(ls.lesson_id, ch.chapter_id)}>
                  <span className="edu-lesson-ix">
                    {ls.complete ? <Check size={12} /> : <Circle size={12} />}
                  </span>
                  <span className="edu-lesson-ttl">{ls.number} · {ls.title}</span>
                  {ls.is_next && <span className="edu-pill ok">Next</span>}
                  <span className="edu-rt num">{ls.runtime}</span>
                </button>
              ))}
            </div>
          )}
          </div>
          );
        })}
        {/* THE CREDITS, the course's copyright page (Randall, 2026-09-24). The server owns the
            wording (backend/course_credits.py) and every curriculum payload carries it, so it is
            here for every enrolled fitter on every track -- at the foot of the table of contents,
            where a printed book keeps its front matter's small print. Renders nothing if absent. */}
        {data.credits?.lines?.length > 0 && (
          <footer className="edu-credits" data-testid="edu-credits">
            <h4 className="edu-credits-h">{data.credits.heading || "Credits"}</h4>
            {data.credits.lines.map((c) => (
              <p key={c.key} className="edu-credits-line" data-testid={`edu-credit-${c.key}`}>
                {c.text}
              </p>
            ))}
          </footer>
        )}
      </div>
    </>
  );
}

/**
 * ONE SCREEN AT A TIME. Randall's ruling, 2026-08-22.
 *
 * EVERYTHING ELSE IN THE SYSTEM WAS ALREADY PER-SCREEN — `screen_count`, `runtime_seconds` on
 * each segment, one narration file per screen. Only the renderer treated a lesson as a single
 * page, and at 42 words a screen nobody noticed. At 330 it produced a 12.8-screen wall with the
 * audio players stranded 7,900px below the prose they narrate and "Mark complete" sitting ABOVE
 * the lesson, reachable by not scrolling.
 *
 * NOTHING ABOUT THE DATA CHANGES. Screens keep their order, their ids and their `screen_type`;
 * this reads `l.segments` exactly as the server sends it. Retired screens were already dropped
 * server-side, so the sequence here is the sequence the student is meant to walk.
 *
 * THE PER-SCREEN WAIT IS PACING, NOT THE GATE, and the distinction is not cosmetic. The server's
 * gate is keyed on `lesson_id` alone — one `served_at` row per lesson — so it cannot refuse an
 * individual screen no matter what this component does. What it CAN refuse is the completion,
 * and that is untouched. So each screen's wait is derived as its SHARE of the lesson's
 * `min_view_seconds`, proportional to its own `runtime_seconds`: walking every screen at its own
 * pace adds up to exactly the requirement the server enforces at the end.
 *
 * NEXT NEVER DEPENDS ON AUDIO. Standing rule, and it matters more now the player is adjacent: a
 * student who reads faster than Randy talks, or who has no audio output at all, must still
 * advance. No audio state is read anywhere in this component.
 */
const SCREEN_MIN_SECONDS = 3;

function screenWaits(segments, minViewSeconds) {
  const total = segments.reduce((a, s) => a + (Number(s?.runtime_seconds) || 0), 0);
  return segments.map((s) => {
    const secs = Number(s?.runtime_seconds) || 0;
    if (!total || !minViewSeconds) return SCREEN_MIN_SECONDS;
    // A knowledge check carries no claimed content time by design, so it gets the floor
    // rather than a share — answering a question is not reading time.
    return Math.max(SCREEN_MIN_SECONDS, Math.round((minViewSeconds * secs) / total));
  });
}

function LessonScreens({ lessonId, segments, minViewSeconds, narrationPending,
                         checkAnswers, complete, footer, onScreenChange, feedback }) {
  const key = `edu-screen:${lessonId}`;
  const last = Math.max(0, segments.length - 1);

  // POSITION SURVIVES THE SESSION. A fitter interrupted mid-lesson returns to the screen they
  // were on. sessionStorage, the same scope the audio position uses — deliberately not
  // localStorage: next week's visit should start at the beginning.
  const [idx, setIdx] = useState(() => {
    // A FINISHED LESSON OPENS AT THE BEGINNING. Randall's ruling, 2026-08-25.
    //
    // The restore below exists to resume an INTERRUPTION — a fitter who stopped halfway comes
    // back to the screen they were on. A completed lesson has no interruption to resume, and
    // the stored position for one is always its LAST screen, which in every authored lesson is
    // the knowledge check. So "go back and look at a section I completed" landed the student on
    // the quiz question every time, with the prose behind them.
    //
    // Only the INITIAL position is affected: this reads once, on mount. Finishing a lesson you
    // are reading does not yank you to screen 1, because `complete` flipping true re-renders
    // rather than remounts.
    if (complete) return 0;
    try {
      const v = parseInt(window.sessionStorage.getItem(key) || "0", 10);
      return Number.isFinite(v) ? Math.min(Math.max(v, 0), last) : 0;
    } catch { return 0; }
  });
  useEffect(() => {
    try { window.sessionStorage.setItem(key, String(idx)); } catch { /* private mode */ }
  }, [key, idx]);
  // The poster card belongs to screen 1 only, so the player above has to know where we are.
  useEffect(() => { onScreenChange?.(idx); }, [idx, onScreenChange]);

  const waits = useMemo(() => screenWaits(segments, minViewSeconds),
                        [segments, minViewSeconds]);

  // THE WAIT IS FOR A SCREEN'S FIRST VISIT ONLY (Scott #6, Kasey UX-37). It used to restart on
  // every visit, so Back to re-read one line cost the whole wait again on the way forward.
  // `reached` is the highest screen the student has LEFT, by Next or by Back; every screen up
  // to it has been seen, and none of them waits again. Updated in `go`, not in an effect, so it
  // moves in the same render as `idx` and the clock below runs once per arrival. The server's
  // completion gate is untouched, so revisits cannot shorten the lesson's required time.
  const reachedKey = `edu-reached:${lessonId}`;
  const [reached, setReached] = useState(() => {
    try {
      const v = parseInt(window.sessionStorage.getItem(reachedKey) ?? "", 10);
      if (Number.isFinite(v)) return Math.min(v, last);
    } catch { /* private mode */ }
    return idx - 1;           // a restored position with no record: earlier screens were passed
  });
  useEffect(() => {
    try { window.sessionStorage.setItem(reachedKey, String(reached)); } catch { /* private mode */ }
  }, [reachedKey, reached]);

  const [left, setLeft] = useState(0);
  const [filmEndedAt, setFilmEndedAt] = useState(-1);     // the screen whose film has played to its end
  useEffect(() => {
    if (complete || idx <= reached) { setLeft(0); return; }
    const need = waits[idx] || 0;
    const from = Date.now();
    setLeft(need);
    const t = setInterval(() => {
      const rem = Math.ceil(need - (Date.now() - from) / 1000);
      setLeft(rem > 0 ? rem : 0);
      if (rem <= 0) clearInterval(t);
    }, 500);
    return () => clearInterval(t);
  }, [idx, waits, complete, reached]);

  // A new screen starts at the top of itself, not wherever the last one left the page.
  // THE LESSON SCROLLS INSIDE `.edu-body.scroll`, NOT THE WINDOW (Scott #4, #5): this used to
  // scroll only the window, which does not scroll, so Next kept the old offset and landed at the
  // bottom of the next screen. Reset the container (and the window, for any layout where it
  // scrolls) AFTER the new screen renders, and instantly: a smooth scroll started before the
  // re-render was cut short by it, measured at 4760 -> 4480px.
  const wrapRef = useRef(null);
  const firstIdx = useRef(true);
  useEffect(() => {
    if (firstIdx.current) { firstIdx.current = false; return; }
    const box = wrapRef.current?.closest(".edu-body");
    if (box) box.scrollTop = 0;
    try { window.scrollTo(0, 0); } catch { /* older browsers */ }
  }, [idx]);

  const sg = segments[idx];
  if (!sg) return null;
  const onLast = idx >= last;
  const go = (n) => {
    setReached((r) => Math.max(r, idx));
    setIdx(Math.min(Math.max(n, 0), last));
  };

  return (
    <div className="edu-screen-wrap" data-testid="edu-screen-wrap" ref={wrapRef}>
      <div className="edu-screen-progress">
        <span data-testid="edu-screen-count">Screen {idx + 1} of {segments.length}</span>
        <div className="edu-bar" aria-hidden="true">
          <i style={{ width: `${((idx + 1) / segments.length) * 100}%` }} />
        </div>
        {/* AT THE TOP OF THE SCREEN, NOT UNDER IT (2026-09-01). This first shipped inside the
            footer beside "Mark complete" — which on a long screen is below the fold, and
            Randall could not find it walking his own course. A control a tester has to hunt for
            is a control that does not get used, and the whole value of it is that the note
            arrives attached to the exact screen. It now sits in the progress row, in view the
            moment the screen renders and next to the position indicator it refers to. */}
        {feedback && feedback(idx)}
      </div>

      {/* ONE SCREEN, ONE SET OF STATE. The key is the fix for UX-31 (Scott #13-20, Bill #41,
          Kasey's run): when faf0838 paginated the lesson on 21 Aug, the per-screen <div key={i}>
          went with the list, so this card stayed mounted while `idx` changed and every stateful
          child carried its state onto the next screen. Commit Case I, press Next, and Case II
          arrived already committed -- form gone, answer shown. The 9 Sept batch fixed the
          spoiler titles, a real but different defect, and closed the rows on a data check.
          Keyed by lesson AND screen so a Back or a new lesson also starts clean. */}
      <div className="edu-card edu-screen" data-testid="edu-screen" key={`${lessonId}:${idx}`}
           data-screen-index={idx} data-screen-type={sg.screen_type}>
        {/* THE TITLE IS READ ALOUD, SO IT HAS TO LOOK LIKE THE THING BEING READ.
            Reported by a tester 2026-09-09: the narration opens by announcing the screen
            title, but the title was set in `edu-eyebrow` -- 10px, uppercase, muted -- and the
            play button sat BELOW the image. Press play and the only thing near your eye is a
            picture, so correct narration reads as though it is out of sync. It is a heading
            now, and the player sits directly under it, above the figure. */}
        {sg.title && <h2 className="edu-screen-title">{sg.title}</h2>}

        {/* THE PLAYER SITS WITH THE PASSAGE IT NARRATES. That is the whole point of the ruling:
            a student arrives, presses play, and hears Randy read what is in front of them. */}
        <LessonAudio slug={sg.audio_slug} title={sg.title} />

        {/* No attemptId here - a lesson screen is not a graded quiz, so the tutor
            answers freely. */}
        <div className="edu-stuck-mount" data-testid="edu-stuck-mount-lesson">
          <StuckHelp lessonId={lessonId} screenIndex={idx} />
        </div>

        {/* A FILM AND A FIGURE ARE NOT ALTERNATIVES (found 2026-09-24, as a fitter sees it).
            This was video-OR-figure, so ch4 L2 s1 "Sole radius sets the sensitivity" -- which
            carries the Bending film AND the sole-radius figures Randall placed there -- drew the
            film and silently dropped the figure; the route check passed because the slug was in
            the data. Each renders when present, film first as before. A case screen still draws
            its own figure inside the case, not here. */}
        {sg.video_url && <LessonVideo url={sg.video_url} caption={sg.video_caption}
                                      transcript={sg.video_transcript}
                                      onEnded={() => setFilmEndedAt(idx)} />}
        {/* WHEN THE FILM ENDS, THE WAY ON IS RIGHT THERE (feedback #27, 2026-10-16). A fitter who watched a film to the
            end found nothing to press: Next sat below the whole screen's text (2,200 px down on a laptop, 2,900 px on
            a phone) and Back is disabled on a lesson's first screen. The same two buttons, with the same rules and the
            same reading wait, now appear under the film once it has played. Navigation only: nothing is completed. */}
        {sg.video_url && filmEndedAt === idx && (
          <div className="edu-film-end" data-testid="edu-film-end">
            <span className="edu-p faint">End of the film.</span>
            <button type="button" className="edu-btn ghost" onClick={() => go(idx - 1)}
                    disabled={idx === 0} data-testid="edu-film-end-back">
              <ChevronLeft size={14} /> Back
            </button>
            {onLast ? (
              <button type="button" className="edu-btn" data-testid="edu-film-end-finish"
                      onClick={() => wrapRef.current?.querySelector(".edu-screen-nav")
                                       ?.scrollIntoView({ behavior: "smooth", block: "center" })}>
                Finish the lesson <ChevronRight size={14} />
              </button>
            ) : (
              <button type="button" className="edu-btn" onClick={() => go(idx + 1)}
                      disabled={left > 0} data-testid="edu-film-end-next">
                {left > 0 ? `Next in ${left}s` : "Next"} <ChevronRight size={14} />
              </button>
            )}
          </div>
        )}
        {!sg.case && (sg.image_url || sg.image_slug) && (
          <LessonFigure url={sg.image_url} slug={sg.image_slug} caption={sg.image_caption}
                        displayWidth={sg.image_display_width} />
        )}

        {sg.learning_point && (
          <LessonText text={sg.learning_point}
                      pending={narrationPending && !sg.audio_slug
                               && !NEVER_NARRATED.has(sg.screen_type)} />
        )}

        <ReferenceChart chart={sg.chart} />
        <LessonCase seg={sg} index={idx}
                    onAnswer={(a) => { checkAnswers.current[a.screen_index] = a; }} />
        <LessonCheck seg={sg} index={idx}
                     onAnswer={(a) => { checkAnswers.current[a.screen_index] = a; }} />
      </div>

      <div className="edu-screen-nav">
        <button type="button" className="edu-btn ghost" onClick={() => go(idx - 1)}
                disabled={idx === 0} data-testid="edu-screen-back">
          <ChevronLeft size={14} /> Back
        </button>

        {onLast ? footer : (
          <div className="edu-screen-next">
            <button type="button" className="edu-btn" onClick={() => go(idx + 1)}
                    disabled={left > 0} data-testid="edu-screen-next">
              Next <ChevronRight size={14} />
            </button>
            {left > 0 && (
              <span className="edu-p faint" data-testid="edu-screen-wait">
                Next in {left}s — this screen has a minimum reading time.
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ----------------------------------------------------------- Lesson Player
/**
 * REPORT A PROBLEM WITH THE SCREEN YOU ARE ON.
 *
 * THE CONTEXT IS THE FEATURE. A tester writing "chapter 6 was off" costs an afternoon to chase;
 * the same sentence carrying lesson id and screen index is actionable the moment it lands. So
 * every field except the note is filled in from what the player is ALREADY rendering, and the
 * person only types the sentence.
 *
 * It never blocks the course. A failed post shows an error and leaves the note in the box to
 * retry — losing somebody's paragraph because the network hiccupped is the one outcome that
 * would stop them filing the next one.
 */
// WHO SEES "REPORT AN ISSUE": EVERY FITTER (Randall, 2026-10-16). From 2026-09-01 it was the review group's
// alone; the beta is closed and the control stays, open to everyone, on every lesson screen and under every
// quiz question, the final exam included ("a mis-keyed exam item is exactly what you want to hear about").
// The server accepts a note from any enrolled fitter.
export function FeedbackControl({ context }) {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("confusing");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const CATEGORIES = [
    ["bug", "Something broke"],
    ["confusing", "Confusing"],
    ["wrong", "Factually wrong"],
    ["suggestion", "Suggestion"],
  ];

  async function submit(e) {
    e.preventDefault();
    if (!note.trim() || busy) return;
    setBusy(true);
    setErr("");
    try {
      await api("/feedback", {
        method: "POST",
        body: JSON.stringify({ ...context, category, note: note.trim() }),
      });
      setDone(true);
      setNote("");
      setTimeout(() => { setDone(false); setOpen(false); }, 2200);
    } catch (e2) {
      setErr(e2.message || "Could not send that.");
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <button type="button" className="edu-feedback-open" data-testid="edu-feedback-open"
              onClick={() => setOpen(true)}>
        Report an issue
      </button>
    );
  }
  return (
    <form className="edu-feedback" data-testid="edu-feedback" onSubmit={submit}>
      <div className="edu-feedback-head">
        <b>Report an issue</b>
        {/* SHOWN, NOT HIDDEN. The person filing should be able to see exactly what is being
            attached to their note. */}
        <span className="edu-feedback-where">
          {context.screen_title
            ? `screen ${(context.screen_index ?? 0) + 1} — ${context.screen_title}`
            : `screen ${(context.screen_index ?? 0) + 1}`}
        </span>
        <button type="button" className="edu-feedback-close"
                onClick={() => setOpen(false)} aria-label="Close">×</button>
      </div>
      <div className="edu-feedback-cats" role="group" aria-label="Category">
        {CATEGORIES.map(([key, label]) => (
          <button key={key} type="button"
                  className={`edu-feedback-cat${category === key ? " active" : ""}`}
                  aria-pressed={category === key}
                  onClick={() => setCategory(key)}>{label}</button>
        ))}
      </div>
      <textarea className="edu-feedback-note" rows={4} value={note} maxLength={4000}
                placeholder="What did you notice? Be as specific as you like."
                onChange={(e) => setNote(e.target.value)} />
      {err && <p className="edu-feedback-err">{err}</p>}
      {done
        ? <p className="edu-feedback-ok" role="status">Thanks — that went to Henry-Griffitts.</p>
        : <button type="submit" className="edu-btn" disabled={busy || !note.trim()}>
            {busy ? "Sending…" : "Send"}
          </button>}
    </form>
  );
}

function LessonPlayer({ lessonId, onBack, openQuiz, reload }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  // WHICH SCREEN IS SHOWING. Only so the poster card can be limited to the first one --
  // the pager owns the position, this is a read of it.
  const [screenIdx, setScreenIdx] = useState(0);
  const onScreenChange = useCallback((i) => setScreenIdx(i), []);
  // Keyed by screen index so a re-answer replaces rather than appends.
  const checkAnswers = useRef({});

  // Curriculum resolves the lesson id before it navigates, so the player is only ever asked
  // for a lesson it can actually load. The old code path here tried to resolve a chapter to a
  // lesson and unconditionally produced null, which is why the click appeared to do nothing.
  const load = useCallback(async (id) => {
    if (!id) { setErr("No lesson was selected."); return; }
    try {
      setErr("");
      setData(await api(`/lessons/${id}`));
    } catch (e) { setErr(e.message); }
  }, []);

  useEffect(() => { if (lessonId) load(lessonId); }, [lessonId, load]);

  // THE MINIMUM-TIME GATE, SHOWN AS A REASON RATHER THAN A DEAD BUTTON.
  //
  // THE SERVER OWNS WHETHER THE REQUIREMENT IS MET. This used to seed the countdown with the
  // full `min_view_seconds` and start it from Date.now() on every payload load — so the clock
  // restarted on every visit while the server's gate accumulated from the EARLIEST served_at.
  // A lesson satisfied on 17 August still rendered "Available in 223s" and refused the click,
  // a wall the server would never have imposed. Measured on the walkthrough account: two
  // visits moments apart, both 223s.
  //
  // `view_gate.remaining` is the same computation the completion endpoint enforces, so the
  // button and the refusal cannot disagree. It is 0 once satisfied, and the countdown then has
  // nothing to count. The timing FORMULA is untouched — this only stops the client inventing
  // its own clock.
  const [waitLeft, setWaitLeft] = useState(null);
  useEffect(() => {
    const gate = data?.view_gate;
    if (!gate || gate.ok || data?.complete) { setWaitLeft(null); return; }
    const remaining = Math.max(0, Number(gate.remaining) || 0);
    if (!remaining) { setWaitLeft(null); return; }
    const openedAt = Date.now();
    setWaitLeft(remaining);
    const t = setInterval(() => {
      const left = Math.ceil(remaining - (Date.now() - openedAt) / 1000);
      setWaitLeft(left > 0 ? left : null);
      if (left <= 0) clearInterval(t);
    }, 1000);
    return () => clearInterval(t);
  }, [data?.lesson?.lesson_id, data?.view_gate, data?.complete]);

  if (err) return <div className="edu-body edu-error">{err}</div>;
  if (!data) return <div className="edu-body edu-loading"><Loader2 className="spin" /></div>;

  const l = data.lesson;

  const markComplete = async () => {
    setBusy(true);
    try {
      // The knowledge-check answers ride along with the completion the student was
      // going to send anyway. No extra route, no second progress store.
      const checks = Object.values(checkAnswers.current);
      await api(`/lessons/${l.lesson_id}/complete`, {
        method: "POST",
        body: JSON.stringify({ checks }),
      });
      const nxt = (data.siblings || []).find((s) => !s.complete && s.lesson_id !== l.lesson_id);
      reload?.();
      // P0: FINISHING A CHAPTER USED TO DEAD-END. With no next incomplete lesson this called
      // onBack() and dropped the student on the curriculum list - so Randall ticked all six of
      // chapter 1 and was never offered its quiz, which has 53 servable items. The server was
      // never asked; the client simply had nowhere to send him. Now the last lesson leads to
      // the chapter quiz when one can actually be served, and falls back to the curriculum
      // when it cannot rather than routing into a 409.
      // ASK THE GATE AGAIN (2026-09-28). `data.quiz_available` was computed before this
      // completion, when this very lesson was still unread; the answer that counts is the one the
      // server gives now. A closed quiz shows its reason on the lesson rather than opening.
      if (nxt) { setData(null); load(nxt.lesson_id); }
      else {
        const fresh = await api(`/lessons/${l.lesson_id}`).catch(() => null);
        if (fresh?.quiz_available) {
          openQuiz({ trackId: l.track_id, chapterId: l.chapter_id, fromLesson: l.lesson_id });
        } else if (fresh?.quiz?.message) { setData(fresh); }
        else { onBack(); }
      }
    } catch (e) {
      // The server's 409 carries the reason and the seconds remaining. Show THAT, not a
      // generic failure - someone who has read quickly deserves to know what is being asked.
      let msg = e.message;
      try {
        const d = JSON.parse(msg)?.detail || JSON.parse(msg);
        if (d?.message) msg = d.message;
      } catch { /* not JSON, use as-is */ }
      setErr(msg);
    }
    setBusy(false);
  };

  return (
    <>
      <div className="edu-topbar">
        <h3 className="edu-h3 display">{l.title}</h3>
        <div className="edu-crumbs">
          Ch. {data.chapter?.number} · Lesson {l.number} · <b className="num">{data.runtime}</b>
        </div>
      </div>
      <div className="edu-body scroll">
        <button className="edu-btn ghost" onClick={onBack} style={{ marginBottom: 12 }}>
          <ChevronLeft size={14} /> Curriculum
        </button>
        <div className="edu-grid g-73">
          <div>
            {/* THE POSTER INTRODUCES THE LESSON, SO IT BELONGS AT THE START OF IT.
                Randall's ruling 2026-08-22: after screen 1 it is 400px of artwork between a
                student and the next three hundred words, repeated on every screen. Rendering
                only -- nothing about the lesson data knows this exists. */}
            {/* ONLY A REAL FILM. Without a `media_url` this used to draw a 16:9 black "player"
                holding the chapter title -- a video that never plays, and on 19 Sept that was
                every lesson (0 of 89 published lessons carry a media_url). Wave 2 placeholder
                sweep: the fake is gone; a lesson with a film still gets it on screen 1. */}
            {screenIdx === 0 && l.media_url && (
            <div className="edu-player" data-testid="edu-poster">
              <video controls src={l.media_url} style={{ width: "100%", height: "100%" }} />
            </div>
            )}
            {screenIdx === 0 && l.content_status === "needs_remaster" && (
              <div className="edu-remaster-badge" data-testid="edu-remaster"
                   style={{ marginBottom: 12 }}>
                <AlertTriangle size={13} /> Being remastered — read the lesson below
              </div>
            )}
            {/* COMPLETION MOVED TO THE END OF THE LESSON. It used to sit here, above the
                content — on a 12.8-screen lesson a student reached it by NOT scrolling. It is
                now the last screen's footer, reached by finishing. The server's gate is
                unchanged either way; this only stops the button being easier to find than the
                lesson. */}
            {/* The "CC" and "Transcript" pills that sat here were plain spans that looked like
                toggles and did nothing: there is no caption track, and each screen's text already
                IS what the narration reads. Removed in the Wave 2 placeholder sweep. */}

            {/* ONE SCREEN AT A TIME. (The block this replaced described the field as "one
                script, three renderings ... this IS the transcript" — it was not, and the
                server's `transcript` key is no longer rendered anywhere.)
                reading version.

                P0 (2026-08-11): the server used to send the AUTHORING SCAFFOLD here — the
                narration rule, "[1] title (3:00 · reading)" markers, "Learning point:" prefixes,
                "Source: manual2016/..." lines and "[Narration to be written...]" TODOs — and this
                block rendered all of it to the student. The scaffold now lives in
                `authoring_notes`, which the lesson route does not return. What arrives is prose,
                so it is rendered as paragraphs, with the sources line and the
                narration-in-production note pulled out and styled rather than left inline. */}
            {/* ONE SCREEN AT A TIME. This replaced a single card holding the whole
                `narration_script` followed by every figure, player, chart and check stacked
                underneath it — which is how the audio players ended up 7,900px from the prose
                they narrate. The words are the same words: the script IS the screens' prose
                concatenated, so rendering per screen loses nothing and puts each passage with
                its own picture and its own player. */}
            <LessonScreens
              lessonId={l.lesson_id}
              segments={l.segments || []}
              minViewSeconds={data.min_view_seconds}
              narrationPending={data.narration_pending}
              checkAnswers={checkAnswers}
              complete={data.complete}
              onScreenChange={onScreenChange}
              /* ONE control, rendered at the top of whichever screen is showing. The index comes
                 from the pager itself, so the context can never drift from what is on screen. */
              feedback={(i) => (
                /* Keyed per screen: unkeyed, a note half-typed on one screen followed the
                   student to the next and was filed against the screen they moved to. */
                <FeedbackControl key={`${l.lesson_id}:${i}`} context={{
                  track_id: l.track_id || data.track_id || "",
                  chapter_id: l.chapter_id || "",
                  lesson_id: l.lesson_id,
                  screen_index: i,
                  screen_title: (l.segments || [])[i]?.title || "",
                }} />
              )}
              footer={(
                <div className="edu-screen-next">
                  <button className="edu-btn" onClick={markComplete}
                    disabled={busy || data.complete || !!waitLeft}
                    data-testid="edu-mark-complete">
                    {busy ? <Loader2 size={14} className="spin" /> : <Check size={14} />}
                    {data.complete ? "Completed" : "Mark complete & continue"}
                  </button>
                  {!data.complete && !!waitLeft && (
                    <span className="edu-p faint" data-testid="edu-min-time">
                      Available in {waitLeft}s — this lesson has a minimum reading time.
                    </span>
                  )}
                </div>
              )}
            />
            {/* GATED ON AUDIO, NOT ON THE SCRIPT. `narration_pending` is
                `STUDENT_PENDING_NOTE in narration_script`, and the scripts were never
                re-stamped when the clips landed — so all six chapter-1 lessons still
                announced that their narration was pending while serving twenty real clips of
                Randy's voice underneath the banner.

                Stripping the note from `narration_script` would fix the flag and change
                `text_hash`, and the generator is idempotent on `text_hash` — all 89 clips
                would re-bill. It is a display bug and it is fixed in the display, exactly as
                the per-screen variant already does it (`narrationPending && !sg.audio_slug`).
                A lesson with any narrated screen does not claim to be waiting for narration. */}
            {data.narration_pending
              && !(l.segments || []).some((s) => s.audio_slug) && (
              <p className="edu-pending" data-testid="edu-narration-pending-lesson">
                Narration is in production — the text on each screen is the lesson's content.
              </p>
            )}
          </div>

          <div>
            <div className="edu-card tight">
              <div className="edu-eyebrow">This lesson</div>
              <div className="edu-crit">Runtime<span className="edu-val num">{data.runtime}</span></div>
              <div className="edu-crit">Counts toward PGA
                <span className="edu-val">{l.counts_toward_pga ? "Yes" : "No"}</span></div>
              <div className="edu-crit">Format<span className="edu-val">{l.format}</span></div>
              {/* NARRATION STATUS FROM THE SCREENS, NOT FROM A STORED FIELD. This read
                  `narration_voice`, which is still '' on every lesson, so it printed "Pending"
                  on a lesson carrying four generated audio files. A status field that nobody
                  updates is worse than no status field: it tells the student the opposite of
                  what the page is showing them. */}
              <div className="edu-crit">Narration
                <span className="edu-val">
                  {(l.segments || []).some((sg) => sg && sg.audio_slug)
                    ? (l.narration_voice || "Randy Henry")
                    : "Pending"}
                </span></div>
              <div className="edu-crit">Screens<span className="edu-val num">{l.screen_count}</span></div>
            </div>
            <div className="edu-card tight" style={{ marginTop: 14 }}>
              <div className="edu-eyebrow">Chapter</div>
              {(data.siblings || []).map((s) => (
                <button key={s.lesson_id} type="button"
                  className={`edu-crit link ${s.lesson_id === l.lesson_id ? "cur" : ""}`}
                  aria-current={s.lesson_id === l.lesson_id ? "true" : undefined}
                  onClick={() => { setData(null); load(s.lesson_id); }}>
                  {s.complete ? <Check size={13} /> : <Circle size={13} />} {s.number} · {s.title}
                </button>
              ))}
              {/* NEVER OFFER A QUIZ THE SERVER WILL REFUSE (2026-09-28). The button used to open
                  the quiz whatever the gate said, and the refusal that followed is what stopped
                  Wayne Thomas. Availability is the gate's own answer; a closed quiz says why. */}
              <button className="edu-btn ghost" style={{ marginTop: 10, width: "100%" }}
                disabled={!data.quiz_available} data-testid="edu-chapter-quiz"
                onClick={() => openQuiz({ trackId: l.track_id, chapterId: l.chapter_id,
                                          fromLesson: l.lesson_id })}>
                <FileText size={13} /> Chapter quiz
              </button>
              {!data.quiz_available && data.quiz?.message && (
                <div className="edu-p faint" style={{ marginTop: 8 }} data-testid="edu-chapter-quiz-closed">
                  <b>{data.quiz.message}</b>{data.quiz.body ? <> {data.quiz.body}</> : null}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// -------------------------------------------------------------------- Quiz
// ------------------------------------------------------------- My Record (the transcript)
//
// THE STUDENT'S OWN RECORD (Wave 3, 2026-09-22), built on GET /transcript. It is a SCREEN and
// not a document: no letterhead, no seal, nothing calling itself official. Whether HG issues a
// transcript a fitter hands to the PGA or an employer is Randall's decision, and so is what such
// a document would say.
//
// It shows every attempt rather than the best, for the reason the route gives: a record that
// silently drops the failures is not a record.
function MyRecord() {
  const [t, setT] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let live = true;
    api("/transcript")
      .then((d) => { if (live) setT(d); })
      .catch((e) => { if (live) setErr(e.message); });
    return () => { live = false; };
  }, []);

  if (err) return <div className="edu-body edu-error">{err}</div>;
  if (!t) return <div className="edu-body edu-loading"><Loader2 className="spin" /></div>;

  const track = (t.tracks || [])[0];
  const fmt = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    return Number.isNaN(d.valueOf()) ? null : d.toLocaleDateString();
  };

  return (
    <>
      <div className="edu-topbar">
        <h3 className="edu-h3 display">My Record</h3>
        <div className="edu-crumbs">
          {t.holder?.name || t.holder?.email}
          {track ? <> · <b className="num">{track.chapters_complete}</b> of{" "}
            <b className="num">{track.chapters_total}</b> chapters</> : null}
        </div>
      </div>
      <div className="edu-body scroll" data-testid="edu-record">
        <div className="edu-card">
          <div className="edu-eyebrow">Hours</div>
          <div className="num edu-big">{t.hours?.combined}</div>
          <p className="edu-p muted">
            <span className="num">{t.hours?.online}</span> online ·{" "}
            <span className="num">{t.hours?.in_person}</span> in person
          </p>
          <p className="edu-p faint" style={{ marginTop: 8, fontSize: 12 }}>
            Hours come from the credit ledger. Voided rows are not shown.
          </p>
        </div>

        {t.certification && (t.certification.issued || t.certification.eligible
          || (t.certification.final_exam_passed && t.certification.certification_paused_reason)) && (
          <div className="edu-card pass" data-testid="edu-record-cert">
            <div className="edu-eyebrow">Certification</div>
            <p className="edu-p">
              {t.certification.issued && t.certification.certificate_status === "revoked" ? null
                : !t.certification.issued && !t.certification.eligible ? "Final exam passed."
                : t.certification.issued && t.certification.certificate
                ? <>Issued: <b>{t.certification.certificate.level_display}</b>
                  {t.certification.certificate.certificate_number
                    ? <> · <span className="num">
                      {t.certification.certificate.certificate_number}</span></> : null}</>
                : "Earned — Henry-Griffitts approves each certificate before it is issued."}
            </p>
            <CertificationWhyNot cert={t.certification} testid="edu-record-why-not" />
          </div>
        )}

        {track && track.final_exam?.attempt_count > 0 && (
          <div className="edu-card" data-testid="edu-record-final">
            <div className="edu-eyebrow">Final exam</div>
            <ExamNotice notice={track.final_exam.exam_notice} testid="edu-record-switch-notice" />
            {track.final_exam.attempts.map((a, i) => (
              <p className="edu-p muted" key={`${a.exam || "final"}-${a.attempt_number}-${i}`}>
                {finalAttemptLabel(a, track.final_exam.attempts)} <span className="num">{a.attempt_number}</span> ·{" "}
                <span className="num">{a.score_pct}%</span> ·{" "}
                {a.passed ? "passed" : "not passed"}
                {fmt(a.submitted_at) ? ` · ${fmt(a.submitted_at)}` : ""}
              </p>
            ))}
          </div>
        )}

        {(track?.chapters || []).map((c) => (
          <div className="edu-card" key={c.chapter_id}>
            <div className="edu-eyebrow">
              Chapter {c.number} · {c.title}
              {c.complete && <span className="edu-pill" style={{ marginLeft: 8 }}>complete</span>}
            </div>
            {c.lessons.map((l) => (
              <p className="edu-p muted" key={l.lesson_id}>
                {l.completed_at ? <Check size={14} aria-hidden="true" /> : <Circle size={14} aria-hidden="true" />}
                {" "}{l.title}
                {l.completed_at
                  ? <span className="faint"> · {fmt(l.completed_at)}
                    {l.minutes ? <> · <span className="num">{l.minutes}</span> min</> : null}</span>
                  : <span className="faint"> · not completed</span>}
              </p>
            ))}
            {c.quiz.attempt_count > 0 ? (
              <p className="edu-p" style={{ marginTop: 8 }}>
                Quiz: {c.quiz.attempts.map((a, i) => (
                  <span key={a.attempt_number}>
                    {i ? ", " : ""}
                    <span className="num">{a.score_pct}%</span>
                    {a.passed ? "" : " (not passed)"}
                  </span>
                ))}
              </p>
            ) : (
              <p className="edu-p faint" style={{ marginTop: 8 }}>Quiz: not attempted</p>
            )}
          </div>
        ))}
      </div>
    </>
  );
}

// WHY A PASS IS NOT A CREDENTIAL (Randall 2026-09-30). "Whatever the flag is set to, a fitter who
// passes and does not receive a credential must be told why, in plain words." From 28 to 30 Sept the
// award was closed and the paused reason reached the browser, where nothing displayed it: a fitter
// who passed saw only that the exam was recorded. Two cases a pass does not cover are said here:
//   * the award is paused   -> the server's own reason (certification_paused_reason), verbatim;
//   * the certificate on record is revoked -> said so, rather than "has been issued".
// (Chapters outstanding and "earned, awaiting approval" are said by the callers already.)
// The server words it (E.final_switch_notice); this only shows it.
// THE BOOK IS OPEN, AND THE EXAM SAYS SO (Randall 2026-10-02). Appendix A was always one click away and he still
// could not find it: the only place the app mentioned the Reference panel was the message after a third failed
// attempt. This sits above the first question of every quiz and the final, says what the panel carries, and opens it.
export const OPEN_BOOK = {
  message: "This is open book.",
  carries: "The Reference panel holds the Clubfitting Model, Appendices A to G, the Fit Chart, the formulas, and the " +
           "shaft, head and grip tables. Open it at any time with the Reference button in the menu, or press R. It " +
           "opens over the question and closes back to it.",
  untimed: "There is no time limit.",
};

export function OpenBookNotice({ untimed, onReference }) {
  return (
    <div className="edu-card tight" data-testid="edu-open-book" role="note" style={{ marginBottom: 18 }}>
      <p className="edu-p"><b>{OPEN_BOOK.message}</b>{untimed ? ` ${OPEN_BOOK.untimed}` : ""}</p>
      <p className="edu-p muted" style={{ marginTop: 4 }}>{OPEN_BOOK.carries}</p>
      {onReference && (
        <button className="edu-ref-open" onClick={onReference} data-testid="edu-open-book-reference"
                style={{ margin: "10px 0 0" }}>
          <Library size={16} /> Open the Reference panel
        </button>
      )}
    </div>
  );
}

export function ExamNotice({ notice, testid }) {
  if (!notice || !notice.message) return null;
  return (
    <div className="edu-card tight" data-testid={testid} role="note" style={{ marginBottom: 18 }}>
      <p className="edu-p"><b>{notice.message}</b></p>
      {notice.body && <p className="edu-p muted" style={{ marginTop: 4 }}>{notice.body}</p>}
    </div>
  );
}

// Attempts at the earlier final and at the rebuilt one are numbered separately, so once a record
// holds both, each line says which exam it was. A record with one kind reads as it always did.
export function finalAttemptLabel(a, all) {
  const kinds = new Set((all || []).map((x) => x.exam || "earlier"));
  if (kinds.size < 2) return "Attempt";
  return a.exam === "rebuilt" ? "New exam, attempt" : "Earlier exam, attempt";
}

export function CertificationWhyNot({ cert, testid }) {
  if (!cert) return null;
  if (cert.issued && cert.certificate_status === "revoked") {
    const n = cert.certificate && cert.certificate.certificate_number;
    return (
      <p className="edu-p" style={{ marginTop: 8 }} data-testid={`${testid}-revoked`}>
        Your certificate{n ? <> <span className="num">{n}</span></> : null} has been revoked, so you
        do not hold a credential at the moment. Henry-Griffitts can tell you why.
      </p>
    );
  }
  if (!cert.issued && !cert.eligible && cert.certification_paused_reason) {
    return (
      <p className="edu-p" style={{ marginTop: 8 }} data-testid={`${testid}-paused`}>
        {cert.certification_paused_reason}
      </p>
    );
  }
  return null;
}

// WHAT HAPPENS AFTER THE FINAL EXAM (Wave 3, 2026-09-22).
//
// EVERY SENTENCE HERE IS SOMETHING THE SERVER ALREADY DOES, and nothing here is a promise about
// timing, contact or anything else nobody has ruled on. The exam is graded and the attempt is
// recorded; issuing a certificate is an admin approval (POST /admin/certification/approve),
// which requires a confirmed engraving name and emails the holder when it happens. Eligibility
// is read from GET /certification/eligibility rather than assumed from a pass, because
// eligibility is "every chapter complete AND a passed final" — a candidate can pass the exam
// with a chapter outstanding, and then the pass is not yet a credential.
function FinalExamNext({ result }) {
  const [elig, setElig] = useState(null);
  const [eligErr, setEligErr] = useState(false);

  useEffect(() => {
    if (!result.passed) return undefined;
    let live = true;
    // Eligibility was otherwise fetched only at sign-in, so a candidate who passed saw nothing
    // change until they reloaded. Fetch it here, on the screen where it just changed.
    api("/certification/eligibility")
      .then((d) => { if (live) setElig(d); })
      .catch(() => { if (live) setEligErr(true); });
    return () => { live = false; };
  }, [result.passed]);

  if (!result.passed) {
    const left = result.attempts_remaining;
    const cooldown = result.cooldown_until ? new Date(result.cooldown_until) : null;
    const cooldownOver = cooldown && !Number.isNaN(cooldown.valueOf()) && cooldown > new Date();
    return (
      <div data-testid="edu-final-fail">
        <p className="edu-p muted">
          A retake draws a new form from the exam bank.
        </p>
        {/* The rebuilt final (Randall 2026-09-29) resets itself after 30 days; the server words it. */}
        {result.lockout ? (
          <div data-testid="edu-final-lockout" style={{ marginTop: 8 }}>
            <p className="edu-p">{result.lockout.message}</p>
            <p className="edu-p muted" style={{ marginTop: 4 }}>{result.lockout.body}</p>
            {result.lockout.study && (
              <p className="edu-p" style={{ marginTop: 4 }}>{result.lockout.study}</p>
            )}
          </div>
        ) : (
          <p className="edu-p" style={{ marginTop: 8 }}>
            {left === 0
              ? "You have used all three attempts. Henry-Griffitts has to reset the exam before it can be sat again."
              : `Attempt ${result.attempt_number} of ${result.attempts_allowed}. ` +
                `${left} attempt${left === 1 ? "" : "s"} remaining.`}
          </p>
        )}
        {cooldownOver && (
          <p className="edu-p faint" style={{ marginTop: 8, fontSize: 12 }}>
            The next attempt can be started after {cooldown.toLocaleString()}.
          </p>
        )}
      </div>
    );
  }

  const chaptersLeft = elig && elig.chapters
    ? Math.max(0, elig.chapters - (elig.chapters_complete || 0)) : null;
  // THE QUIZZES ARE REQUIRED FOR COMPLETION (Randall, 2026-10-05): name the ones still to pass, by chapter.
  const quizzesLeft = (elig && elig.quizzes_outstanding) || [];
  return (
    <div data-testid="edu-final-pass">
      <p className="edu-p muted">
        The certification exam is passed. It is recorded against your name.
      </p>
      {elig && elig.issued && elig.certificate && elig.certificate_status !== "revoked" && (
        <p className="edu-p" style={{ marginTop: 8 }} data-testid="edu-final-issued">
          Your certificate has been issued: <b>{elig.certificate.level_display}</b>
          {elig.certificate.certificate_number
            ? <> · <span className="num">{elig.certificate.certificate_number}</span></> : null}
        </p>
      )}
      {elig && !elig.issued && elig.eligible && (
        <p className="edu-p" style={{ marginTop: 8 }} data-testid="edu-final-eligible">
          Every chapter is complete, every chapter quiz is passed and the exam is passed, so the
          credential is earned.
          Henry-Griffitts approves each certificate itself — it is not issued automatically —
          and confirms the name to be engraved before it is. You will be emailed when it is
          approved.
        </p>
      )}
      <CertificationWhyNot cert={elig} testid="edu-final-why-not" />
      {elig && !elig.eligible && chaptersLeft ? (
        <p className="edu-p" style={{ marginTop: 8 }} data-testid="edu-final-chapters-left">
          {chaptersLeft} chapter{chaptersLeft === 1 ? " is" : "s are"} still outstanding. A
          certificate needs every chapter finished as well as this exam.
        </p>
      ) : null}
      {elig && !elig.eligible && quizzesLeft.length ? (
        <p className="edu-p" style={{ marginTop: 8 }} data-testid="edu-final-quizzes-left">
          {quizzesLeft.length === 1 ? "One chapter quiz is" : `${quizzesLeft.length} chapter quizzes are`} still
          to pass (chapter{quizzesLeft.length === 1 ? "" : "s"} {quizzesLeft.join(", ")}). {quizzesLeft.length === 1 ? "It" : "They"} must
          be passed to complete the course.
        </p>
      ) : null}
      {eligErr && (
        <p className="edu-p faint" style={{ marginTop: 8, fontSize: 12 }}>
          Your certification status could not be loaded just now. The exam result above is
          recorded either way.
        </p>
      )}
    </div>
  );
}

function Quiz({ spec, onBack, onCurriculum, backLabel = "Back", onReference = null }) {
  const [attempt, setAttempt] = useState(null);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");            // fatal: no attempt to work with
  const [submitErr, setSubmitErr] = useState(""); // recoverable: answers are still held
  const [busy, setBusy] = useState(false);
  const [left, setLeft] = useState(null);
  const [autoSubmitted, setAutoSubmitted] = useState(false);
  const stemRef = useRef(null);
  const autoRef = useRef(false);
  const optRefs = useRef([]);
  const answersRef = useRef(answers);
  answersRef.current = answers;

  // ---- open the attempt: resume the one parked in sessionStorage before starting a new one.
  // A mid-quiz refresh must not abandon the attempt (and on a final exam, abandoning it burns
  // one of three chances and starts the cooldown).
  useEffect(() => {
    let dead = false;
    (async () => {
      const parked = attemptStore.get(spec);
      if (parked) {
        try {
          const a = await api(`/quiz/attempt/${parked}`);
          if (dead) return;
          if (a.state === "in_progress") {
            setAttempt({ ...a, resumed: true });
            // the work, not just the form: the server's saved copy, overlaid by anything this
            // tab still holds that is newer than the last checkpoint
            setAnswers({ ...fromSaved(a.saved_responses), ...attemptStore.getAnswers(parked) });
            setLeft(a.seconds_remaining);
            return;
          }
          // submitted or expired while we were away — show the result rather than a new quiz
          attemptStore.clear(spec);
          setAttempt(a);
          setResult(a);
          return;
        } catch {
          attemptStore.clear(spec);   // stale or voided id; fall through to a fresh start
        }
      }
      try {
        const a = await api("/quiz/start", {
          method: "POST",
          body: JSON.stringify({
            track_id: spec.trackId, chapter_id: spec.chapterId || null,
            kind: spec.kind || "chapter_quiz", count: spec.kind === "final_exam" ? 20 : 8,
          }),
        });
        if (dead) return;
        attemptStore.set(spec, a.attempt_id);
        setAttempt(a);
        // A RELAUNCH AFTER A CRASH LANDS HERE, not above: sessionStorage went with the old
        // session, so there was no parked id. /quiz/start hands back the open attempt with the
        // answers the server saved and the time actually left (2026-09-18).
        if (a.resumed && a.saved_responses) setAnswers(fromSaved(a.saved_responses));
        setLeft(a.time_limit_seconds > 0
          ? (a.seconds_remaining ?? a.time_limit_seconds) : null);
      } catch (e) {
        // THE REFUSAL NAMES THE QUIZ (2026-09-28): a structured refusal renders as its heading
        // and its sentence, e.g. "Back to the lessons first." and what to do about it.
        if (!dead) setErr(e.detail ? { message: e.detail.message, body: e.detail.body } : e.message);
      }
    })();
    return () => { dead = true; };
  }, [spec]);

  const items = attempt?.items || [];
  const q = items[idx];

  // ---- submit. On failure the answers are KEPT and a retry is offered; an attempt the server
  // has already graded is not an error, it is a result we have not fetched yet.
  const submit = useCallback(async (reason = "student") => {
    if (!attempt || busy) return;
    setBusy(true);
    setSubmitErr("");
    try {
      const r = await api("/quiz/submit", {
        method: "POST",
        body: JSON.stringify({
          attempt_id: attempt.attempt_id,
          responses: toResponses(attempt.items, answers),
        }),
      });
      attemptStore.clear(spec);
      if (reason === "time_limit") setAutoSubmitted(true);
      setResult(r);
    } catch (e) {
      if (e.status === 400 && /already submitted/i.test(e.message)) {
        try {
          const a = await api(`/quiz/attempt/${attempt.attempt_id}`);
          attemptStore.clear(spec);
          setResult(a);
        } catch (e2) { setSubmitErr(e2.message); }
      } else {
        setSubmitErr(
          reason === "time_limit"
            ? `Time is up, but the submit did not reach the server (${e.message}). Your answers are still here — retry.`
            : `${e.message} — your answers are still here.`);
      }
    }
    setBusy(false);
  }, [attempt, answers, busy, spec]);

  // ---- the countdown. Timed attempts (the final exam) auto-submit once, at zero.
  useEffect(() => {
    if (!attempt || result) return;
    if (!(attempt.time_limit_seconds > 0)) return;
    const t = setInterval(() => {
      setLeft((prev) => {
        const n = (prev ?? attempt.time_limit_seconds) - 1;
        if (n <= 0 && !autoRef.current) { autoRef.current = true; submit("time_limit"); }
        return Math.max(0, n);
      });
    }, 1000);
    return () => clearInterval(t);
  }, [attempt, result, submit]);

  useEffect(() => {
    if (attempt?.attempt_id && !result) attemptStore.setAnswers(attempt.attempt_id, answers);
  }, [answers, attempt?.attempt_id, result]);

  // ---- THE CHECK-IN (2026-09-18). Every CHECKPOINT_MS while the attempt is open, and shortly
  // after any answer changes: the answers go to the server, and the server's remaining time
  // comes back. While these arrive the exam clock is charged; when they stop -- the app died,
  // the window closed, the student was thrown out of the exam -- the server stops the clock
  // after a 90-second grace, up to a 10-minute cap. Deliberately NOT paused on visibility: a
  // hidden exam keeps checking in (Chrome throttles it to about once a minute, inside the
  // grace) and keeps being charged, so hiding it to look something up buys nothing.
  // A failed check-in is silent: the next one carries the same answers.
  const checkpoint = useCallback(async () => {
    if (!attempt?.attempt_id || result) return;
    try {
      const r = await api("/quiz/checkpoint", {
        method: "POST",
        body: JSON.stringify({ attempt_id: attempt.attempt_id,
                               responses: toResponses(attempt.items, answersRef.current) }),
      });
      if (r.state === "expired" || r.state === "submitted") {
        attemptStore.clear(spec);
        if (r.state === "expired") setAutoSubmitted(true);
        setResult(r.state === "expired" ? r
          : await api(`/quiz/attempt/${attempt.attempt_id}`));
        return;
      }
      if (typeof r.seconds_remaining === "number") setLeft(r.seconds_remaining);
    } catch { /* offline for a moment; the next check-in retries */ }
  }, [attempt, result, spec]);

  useEffect(() => {
    if (!attempt?.attempt_id || result) return undefined;
    const t = setInterval(checkpoint, CHECKPOINT_MS);
    return () => clearInterval(t);
  }, [attempt?.attempt_id, result, checkpoint]);

  useEffect(() => {
    if (!attempt?.attempt_id || result) return undefined;
    const t = setTimeout(checkpoint, CHECKPOINT_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [answers, attempt?.attempt_id, result, checkpoint]);

  // Moving between questions moves focus to the new stem, so a keyboard user is not left
  // on a button that now belongs to a different question.
  useEffect(() => { optRefs.current = []; stemRef.current?.focus(); }, [idx]);

  if (err) return (
    <div className="edu-body edu-error" data-testid="edu-quiz-refused">
      {typeof err === "object"
        ? <>
            <h3 className="edu-h3" data-testid="edu-quiz-refused-title">{err.message}</h3>
            {err.body && <p className="edu-p" data-testid="edu-quiz-refused-body">{err.body}</p>}
          </>
        : err}
      <button className="edu-btn ghost" onClick={onBack} style={{ marginTop: 12 }}
              data-testid="edu-quiz-back">{backLabel}</button>
    </div>
  );
  if (!attempt) return <div className="edu-body edu-loading"><Loader2 className="spin" /></div>;

  // ---- results view: every answer teaches ----
  if (result) {
    // `result.kind` is the server's word for which instrument this was; `spec.kind` is the
    // fallback for a result fetched before the field existed.
    const isFinal = (result.kind || spec?.kind) === "final_exam";
    return (
      <>
        <div className="edu-topbar">
          <h3 className="edu-h3 display">{result.passed ? "Passed" : "Not yet"}</h3>
          <div className="edu-crumbs">
            <b className="num">{result.score_pct}%</b> · {result.correct_count} of {result.scored_count}
            {" · "}<PassMark pct={result.pass_mark_pct} label={result.pass_mark_label} note={result.pass_mark_note} />
          </div>
        </div>
        <div className="edu-body scroll">
          <div className={`edu-card ${result.passed ? "pass" : "fail"}`}>
            {/* THE FINAL EXAM IS NOT A CHAPTER (Wave 3, 2026-09-22). This card was shared with
                the chapter quiz, so a candidate who had just passed the certification exam read
                "Chapter complete — you can move on to the next chapter". There is no next
                chapter, and passing issues nothing by itself. `result.kind` is what lets the two
                be told apart, and every sentence the final card states is something the server
                already enforces. */}
            <div className="edu-eyebrow">
              {isFinal && result.trial_note ? "Test sitting"
                : isFinal
                ? (result.passed ? "Final exam passed" : "Final exam — not yet")
                : (result.passed ? "Chapter complete" : "Review and retake")}
            </div>
            <div className="num edu-big">{result.score_pct}%</div>
            {/* A TEST SITTING (Randall 2026-10-02): the server's own note stands in for the attempt line and
                the pass wording, neither of which is true of a sitting that is not an attempt. */}
            {isFinal && result.trial_note
              ? <ExamNotice notice={result.trial_note} testid="edu-final-trial" />
              : isFinal ? <FinalExamNext result={result} /> : (
              <p className="edu-p muted">
                {result.passed
                  ? "You can move on to the next chapter."
                  : `A retake draws a new set of questions from the bank.`}
              </p>
            )}
            {/* The client submits AT zero, so the server records a normal submit and the
                student would otherwise get no explanation for the exam ending by itself.
                Both endings say so. */}
            {(result.finalise_reason === "time_limit_expired" || autoSubmitted) && (
              <p className="edu-p" style={{ marginTop: 8 }} data-testid="edu-expired-note">
                Time ran out, so this attempt was submitted as it stood
                {/* The server's counter, truthful since Round 4 ruling 3 — a blank response
                    row no longer counts as answered. */}
                {result.unanswered_count
                  ? ` with ${result.unanswered_count} question(s) unanswered.` : "."}
              </p>
            )}
          </div>
          {result.review.map((rv, i) => (
            <div key={rv.item_id} className={`edu-card review ${rv.is_correct ? "ok" : "no"}`}>
              <div className="edu-eyebrow">
                Question {i + 1}
                {rv.is_pretest && <span className="edu-pill" style={{ marginLeft: 8 }}>unscored</span>}
                {/* A CAVEAT ON A WRONG ANSWER — this question was marked wrong because an
                    earlier step in the chain was. Not an achievement, so not gold; not a
                    pass, so not green. Neutral, like the `unscored` pill beside it. */}
                {rv.carried_error && (
                  <span className="edu-pill" style={{ marginLeft: 8 }}>
                    chain broke earlier
                  </span>
                )}
              </div>
              <p className="edu-p" style={{ color: "var(--text)" }}>{rv.stem}</p>
              <ItemFigure svg={rv.image_svg} alt={rv.image_url ? rv.stem : ""} />
              {/* SAID IN WORDS (Randall 2026-10-02). The only sign of right or wrong was a two-pixel border, and a
                  correct card's explanation opened with a longer version of the answer, so a fitter who got it
                  right read a correction. The verdict is stated; the explanation under a correct answer no longer
                  restates it (the server sends it without). */}
              <p className={`edu-verdict ${rv.is_correct ? "ok" : "no"}`} data-testid="edu-review-verdict">
                {unanswered(rv) ? "Not answered." : rv.is_correct ? "Correct." : "Not correct."}
              </p>
              <p className="edu-p muted" style={{ marginTop: 6 }}>
                {/* Derived from the answer itself. Cheap, and independent of the counters. */}
                Your answer: {unanswered(rv) ? <i>not answered</i> : String(rv.your_answer)}
              </p>
              {/* P1 (2026-08-11): a wrong answer must be told what the RIGHT one was. The old
                  review explained the distractor you picked and stopped there. Chapter quizzes
                  reveal (a retake redraws anyway); the final exam sends answers_revealed=false
                  and correct_answer=null, so this block simply does not render. */}
              {!rv.is_correct && result.answers_revealed && rv.correct_answer != null && (
                <p className="edu-p" style={{ marginTop: 8 }} data-testid="edu-correct-answer">
                  <b>Correct answer:</b> {String(rv.correct_answer)}
                </p>
              )}
              {rv.feedback && (
                <p className="edu-p" style={{ marginTop: 8 }}>{rv.feedback}</p>
              )}
              {/* The principle behind it. Deduped server-side, so this never repeats the line
                  above. */}
              {rv.principle && rv.principle !== rv.feedback && (
                <p className="edu-p" style={{ marginTop: 8, color: "var(--text)" }}>
                  {rv.principle}
                </p>
              )}
              {rv.manual_citation && (
                <p className="edu-cite">{rv.manual_citation}</p>
              )}
            </div>
          ))}
          <button className="edu-btn" onClick={onCurriculum || onBack} style={{ marginTop: 14 }}>Back to curriculum</button>
        </div>
      </>
    );
  }

  if (!q) return (
    <div className="edu-body edu-error">
      This attempt was served no questions — the bank is empty for this chapter.
      <button className="edu-btn ghost" onClick={onBack} style={{ marginTop: 12 }}
              data-testid="edu-quiz-back">{backLabel}</button>
    </div>
  );

  const cur = answers[q.item_id] || {};
  const pick = (key) => setAnswers({ ...answers, [q.item_id]: { key } });
  const toggle = (key) => {
    const had = cur.keys || [];
    const keys = had.includes(key) ? had.filter((k) => k !== key) : [...had, key];
    setAnswers({ ...answers, [q.item_id]: { keys } });
  };

  // The ARIA keyboard contract for these roles, which buttons do not provide on their own:
  //   radiogroup — arrows move AND select; the group is one tab stop
  //   checkbox   — Space toggles; arrows move focus without selecting
  const onOptionKeys = (e) => {
    const opts = q.options || [];
    const at = optRefs.current.indexOf(e.target);
    const step = (d) => {
      const n = (at + d + opts.length) % opts.length;
      optRefs.current[n]?.focus();
      if (!q.multi_select) pick(opts[n].key);
    };
    if (e.key === "ArrowDown" || e.key === "ArrowRight") { e.preventDefault(); step(1); }
    else if (e.key === "ArrowUp" || e.key === "ArrowLeft") { e.preventDefault(); step(-1); }
    else if (e.key === " " || e.key === "Enter") {
      if (at < 0) return;
      e.preventDefault();
      q.multi_select ? toggle(opts[at].key) : pick(opts[at].key);
    }
  };
  const timed = attempt.time_limit_seconds > 0;
  const lowTime = timed && left !== null && left <= 60;

  return (
    <>
      <div className="edu-topbar">
        <h3 className="edu-h3 display">
          {spec.kind === "final_exam" ? "Final Exam" : "Chapter Quiz"}
        </h3>
        <div className="edu-crumbs">
          Question <b className="num">{idx + 1}</b> of <b className="num">{items.length}</b>
          {" · "}<PassMark pct={attempt.pass_mark_pct} label={attempt.pass_mark_label} note={attempt.pass_mark_note} />
          {timed && (
            <span className={`edu-timer ${lowTime ? "low" : ""}`} data-testid="edu-timer">
              <span className="num">{mmss(left ?? attempt.time_limit_seconds)}</span> left
            </span>
          )}
          {/* On every question, not only the first: the way to the book stays beside the question. */}
          {onReference && (
            <>{" · "}<button className="edu-link" onClick={onReference} data-testid="edu-quiz-reference">Open book: Reference (R)</button></>
          )}
        </div>
      </div>
      {/* Screen-reader announcements: the countdown is not read every second, only at the
          points that change what the student should do. */}
      <div className="edu-sr" role="status" aria-live="polite">
        {attempt.resumed ? "Attempt resumed where you left off. " : ""}
        {lowTime ? "Less than one minute remaining." : ""}
      </div>
      <div className="edu-body scroll">
        <div className="edu-quiz-wrap" data-testid="edu-quiz">
          <div className="edu-bar" style={{ marginBottom: 26 }}>
            <i style={{ width: `${((idx + 1) / items.length) * 100}%` }} />
          </div>
          {/* A NEW EXAM IS A NEW EXAM (Randall 2026-10-01). A fitter who sat the earlier final starts
              the rebuilt one with three attempts. He is TOLD, in the server's words, before the first
              question; nothing is handed back silently. */}
          {attempt.exam_notice && idx === 0 && <ExamNotice notice={attempt.exam_notice} testid="edu-final-switch-notice" />}
          {idx === 0 && <OpenBookNotice untimed={!timed} onReference={onReference} />}
          <div className="edu-eyebrow">Question {idx + 1}</div>
          <h3 className="edu-q-stem" tabIndex={-1} ref={stemRef}>{q.stem}</h3>
          <ItemFigure svg={q.image_svg} alt={q.image_url ? q.image_alt : ""} />

          {/* THE FORMULA SITS WITH THE QUESTION THAT NEEDS IT. A fitter who saw the
              relationship four screens ago and is now asked to use it should not have
              to leave the question to find it again -- that is the phone call this
              card exists to prevent. It is a REFERENCE, not a hint: the worked example
              on the card is always a different club from the one being asked about, so
              the arithmetic is still the student's to do. */}
          {q.reference_card && (
            <aside className="edu-formula-card" data-testid="edu-formula-card">
              <div className="edu-formula-card-label">Reference</div>
              {renderBlocks(q.reference_card)}
            </aside>
          )}

          {q.type === "calculation" ? (
            <div className="edu-numeric">
              {/* Labelled, because a bare box with a placeholder is not a label. */}
              <label className="edu-sr" htmlFor={`num-${q.item_id}`}>
                Your answer{q.numeric_unit ? ` in ${q.numeric_unit}` : ""}
              </label>
              <input id={`num-${q.item_id}`} type="number" step="0.01" placeholder="0.00"
                value={cur.num ?? ""}
                onChange={(e) => setAnswers({ ...answers, [q.item_id]: { num: e.target.value } })}
                data-testid="edu-numeric-input" />
              <span className="edu-faint">{q.numeric_unit}</span>
            </div>
          ) : (
            <div role={q.multi_select ? "group" : "radiogroup"}
              aria-label={q.multi_select ? "Select every answer that applies" : "Answer"}
              onKeyDown={onOptionKeys}>
              {q.multi_select && (
                <p className="edu-p muted" style={{ marginBottom: 10 }}>
                  Select <b>every</b> answer that applies.
                </p>
              )}
              {(q.options || []).map((o, oi) => {
                const sel = q.multi_select
                  ? (cur.keys || []).includes(o.key)
                  : cur.key === o.key;
                return (
                  <button key={o.key} type="button"
                    role={q.multi_select ? "checkbox" : "radio"}
                    aria-checked={sel}
                    ref={(el) => { optRefs.current[oi] = el; }}
                    // A radiogroup's tab stop is the SELECTED option (or the first when
                    // nothing is chosen yet); arrows move within it. Tabbing through every
                    // option one at a time is not the radio pattern.
                    tabIndex={q.multi_select || sel || (!cur.key && oi === 0) ? 0 : -1}
                    className={`edu-card tight edu-option ${sel ? "sel" : ""}`}
                    onClick={() => (q.multi_select ? toggle(o.key) : pick(o.key))}
                    data-testid={`edu-option-${o.key}`}>
                    <div className={`edu-pill ${sel ? "ok" : ""}`}>{o.key}</div>
                    <span className="edu-p">{o.text}</span>
                  </button>
                );
              })}
            </div>
          )}

          {/* attemptId ALWAYS passed here. It is what tells the server this is a live
              graded attempt, which is what keeps the answer key out of the model. */}
          {/* KEYED PER QUESTION (UX-46/47). Without a key this panel is the same instance on
              every question, so one opened on Q3 is still open on Q4 -- in a timed exam, over
              content the student has not read yet. Same defect family as UX-31. */}
          <div className="edu-stuck-mount" data-testid="edu-stuck-mount-quiz">
            <StuckHelp key={q.item_id} itemId={q.item_id} attemptId={attempt?.attempt_id} />
          </div>

          {submitErr && (
            <div className="edu-card fail" style={{ marginTop: 16 }} role="alert"
              data-testid="edu-submit-error">
              <div className="edu-eyebrow">Submit failed</div>
              <p className="edu-p">{submitErr}</p>
              <button className="edu-btn" onClick={() => submit("retry")} disabled={busy}
                style={{ marginTop: 10 }} data-testid="edu-submit-retry">
                {busy ? <Loader2 size={14} className="spin" /> : <Check size={14} />} Retry submit
              </button>
            </div>
          )}

          <div className="edu-actions" style={{ marginTop: 18 }}>
            {idx > 0 && (
              <button className="edu-btn ghost" onClick={() => setIdx(idx - 1)}
                data-testid="edu-prev">Previous</button>
            )}
            {idx < items.length - 1 ? (
              <button className="edu-btn" onClick={() => setIdx(idx + 1)}
                data-testid="edu-next">Next question</button>
            ) : (
              <button className="edu-btn" onClick={() => submit("student")} disabled={busy}
                data-testid="edu-submit">
                {busy ? <Loader2 size={14} className="spin" /> : <Check size={14} />} Submit
              </button>
            )}
          </div>
          {/* A BAD QUESTION IS THE MOST VALUABLE THING A FITTER FINDS (the final exam included), and the moment they see
              it is the moment to capture it. The item is identified by its id: a quiz has no
              lesson or screen, so `screen_title` carries the item id and the note lands on
              something specific rather than on "the chapter 6 quiz". */}
          <FeedbackControl key={q.item_id} context={{
            track_id: spec.trackId || "",
            chapter_id: spec.chapterId || "",
            lesson_id: "",
            screen_index: idx,
            screen_title: `quiz item ${items[idx]?.item_id || ""}`.trim(),
          }} />
        </div>
      </div>
    </>
  );
}

/**
 * THE LESSON HAS AN ADDRESS. Randall's ruling 2026-08-22.
 *
 * CLIENT-SIDE ONLY — no API route is added and the surface stays where it was. The static host
 * already falls back to index.html for unknown paths (verified: /education/lesson/<uuid> returns
 * the 1,269-byte shell), so the History API is all this needs. react-router is a dependency but
 * has never been used for views here, and introducing it would be a restructure of the whole
 * shell rather than the fix asked for.
 *
 * THE URL IS NOT AN ACCESS CONTROL AND MUST NEVER BECOME ONE. Anyone can type any lesson id.
 * What refuses them is `require_chapter_reachable` on the server, which has enforced the
 * sequence since the earlier ruling — this only decides which id the client ASKS for, and a
 * refused lesson renders the server's own message. e2e/lesson-url.mjs proves that with a locked
 * lesson and plants the violation.
 *
 * THE SCREEN INDEX STAYS OUT OF THE URL, deliberately. A shared link — "read this before we
 * meet" — should open at the beginning; where the recipient got to is their own session's
 * business, and that is what sessionStorage already remembers.
 */
/**
 * THE REFERENCE PANEL (Randall, 2026-09-28 / 29). The course is OPEN BOOK throughout, the final
 * exam included, so the reference is one panel in one place -- the rail, which every screen has --
 * that opens OVER what the fitter is doing and closes again. It is a sibling of the screen, not a
 * screen: opening it unmounts nothing, so a quiz underneath keeps its answers, its attempt and its
 * place. Nothing in here posts anything; it only reads GET /api/education/reference.
 *
 * Desktop: a drawer from the right. Phone: a bottom sheet. Search across every entry, shelves to
 * browse, and the last few entries opened (per browser, a convenience only).
 */
const REF_RECENT_KEY = "edu:reference:recent";
const readRecent = () => {
  try { return JSON.parse(localStorage.getItem(REF_RECENT_KEY) || "[]").slice(0, 4); }
  catch { return []; }
};

// A PLAIN FUNCTION, NOT A COMPONENT: a component that renders itself (nested sub-lists) sends the
// @emergentbase/visual-edits Babel plugin, which craco adds for tests and the dev server, into
// unbounded recursion ("Maximum call stack size exceeded"). Production builds do not load it.
function refItem(item, k) {
  if (typeof item === "string") return <li key={k}>{renderInline(item, k)}</li>;
  return (
    <li key={k} className={item.n ? "numbered" : ""}>
      {item.n && <span className="edu-ref-n">{item.n}</span>}
      <span>{renderInline(item.text || "", k)}</span>
      {item.sub?.length > 0 && (
        <ul className={item.sub.some((s) => s && typeof s === "object" && s.n) ? "edu-ref-sub edu-ref-marked" : "edu-ref-sub"}>
          {item.sub.map((s, i) => refItem(s, `${k}-${i}`))}
        </ul>
      )}
      {(item.more || []).map((m, i) => <p key={i} className="edu-ref-more">{renderInline(m, `${k}m${i}`)}</p>)}
    </li>
  );
}

function RefBlock({ b, k }) {
  switch (b.type) {
    case "paragraph": return <p className="edu-ref-p">{renderInline(b.text, k)}</p>;
    case "caution":
      return <div className="edu-ref-caution" role="note"><AlertTriangle size={15} /><span>{b.text}</span></div>;
    case "formula": return <div className="edu-ref-formula">{renderBlocks(b.text)}</div>;
    case "chart": return <div className="edu-ref-chart"><ReferenceChart chart={b.chart} /></div>;
    case "figure": return <LessonFigure slug={b.slug} caption={b.caption} />;
    case "table":
      return (
        <div className="edu-ref-table-wrap">
          {b.caption && <div className="edu-ref-caption">{b.caption}</div>}
          <table className="edu-ref-table">
            <thead><tr>{b.columns.map((c, i) => <th key={i}>{c}</th>)}</tr></thead>
            <tbody>
              {b.rows.map((r, ri) => (
                <tr key={ri}>{r.map((c, ci) => (b.row_header && ci === 0)
                  ? <th key={ci} scope="row">{c}</th> : <td key={ci}>{c}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case "list": {
      // Where the manual prints its own markers ("1)", "A.", "a.") the panel shows those, so a
      // fitter with the printed manual open finds the same numbers.
      const marked = (b.items || []).some((i) => i && typeof i === "object" && i.n);
      const Tag = b.ordered && !marked ? "ol" : "ul";
      const H = b.level === 2 ? "h5" : "h4";
      return (
        <section className="edu-ref-list">
          {b.heading && <H className="edu-ref-h">{b.heading}</H>}
          {b.lead && <p className="edu-ref-p">{renderInline(b.lead, `${k}l`)}</p>}
          {b.items?.length > 0 && (
            <Tag className={marked ? "edu-ref-marked" : ""}>
              {b.items.map((it, i) => refItem(it, `${k}-${i}`))}
            </Tag>
          )}
          {(b.after || []).map((a, i) => <p key={i} className="edu-ref-p">{renderInline(a, `${k}a${i}`)}</p>)}
        </section>
      );
    }
    default: return null;
  }
}

function ReferencePanel({ open, onClose }) {
  const [panel, setPanel] = useState(null);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [shelf, setShelf] = useState(null);
  const [entryId, setEntryId] = useState(null);
  const [recent, setRecent] = useState(readRecent);
  const searchRef = useRef(null);
  const bodyRef = useRef(null);

  // Fetched once, on first open: the panel costs nothing for a fitter who never opens it.
  useEffect(() => {
    if (!open || panel) return;
    api("/reference").then(setPanel).catch((e) => setErr(e.message));
  }, [open, panel]);

  // Focus moves INTO the panel, so the option group underneath (which answers on its own keys)
  // cannot receive a keystroke while the panel is open. Escape closes it.
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => { (entryId ? bodyRef.current : searchRef.current)?.focus(); }, 30);
    const onKey = (e) => { if (e.key === "Escape") { e.stopPropagation(); onClose(); } };
    window.addEventListener("keydown", onKey, true);
    return () => { clearTimeout(t); window.removeEventListener("keydown", onKey, true); };
  }, [open, onClose, entryId, panel]);

  const index = useMemo(() => (panel?.entries || []).map((e) => ({
    e, hay: `${e.title} ${e.summary} ${JSON.stringify(e.blocks)}`.toLowerCase(),
  })), [panel]);

  if (!open) return null;
  const entries = panel?.entries || [];
  const byId = Object.fromEntries(entries.map((e) => [e.id, e]));
  const entry = entryId && byId[entryId];
  const terms = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const hits = terms.length
    ? index.filter(({ hay }) => terms.every((t) => hay.includes(t))).map(({ e }) => e)
    : entries.filter((e) => !shelf || e.shelf === shelf);

  const openEntry = (id) => {
    setEntryId(id);
    const next = [id, ...recent.filter((r) => r !== id)].slice(0, 4);
    setRecent(next);
    try { localStorage.setItem(REF_RECENT_KEY, JSON.stringify(next)); } catch { /* convenience only */ }
  };

  return (
    <div className="edu-ref-scrim" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="edu-ref" role="dialog" aria-modal="true" aria-label="Reference"
           data-testid="edu-reference-panel">
        <div className="edu-ref-top">
          {entry ? (
            <button className="edu-ref-back" onClick={() => setEntryId(null)} data-testid="edu-reference-back">
              <ChevronLeft size={16} /> All reference
            </button>
          ) : <div className="edu-ref-title"><Library size={16} /> Reference <span>open book</span></div>}
          <button className="edu-ref-close" onClick={onClose} aria-label="Close reference"
                  data-testid="edu-reference-close"><X size={18} /></button>
        </div>

        {err ? <div className="edu-ref-body edu-error">{err}</div>
          : !panel ? <div className="edu-ref-body edu-loading"><Loader2 className="spin" /></div>
          : entry ? (
            <div className="edu-ref-body" ref={bodyRef} tabIndex={-1}
                 data-testid={`edu-reference-entry-${entry.id}`}>
              <h3 className="edu-ref-entry-title">{entry.title}</h3>
              {entry.source && <div className="edu-ref-source">{entry.source}</div>}
              {entry.blocks.map((b, i) => <RefBlock key={i} k={`b${i}`} b={b} />)}
            </div>
          ) : (
            <div className="edu-ref-body">
              <input ref={searchRef} className="edu-ref-search" type="search" value={q}
                     onChange={(e) => setQ(e.target.value)} placeholder="Search the reference"
                     aria-label="Search the reference" data-testid="edu-reference-search" />
              {!terms.length && (
                <div className="edu-ref-shelves" role="tablist">
                  {[null, ...panel.shelves].map((s) => (
                    <button key={s || "all"} role="tab" aria-selected={shelf === s}
                            className={`edu-ref-shelf ${shelf === s ? "on" : ""}`}
                            onClick={() => setShelf(s)}>{s || "All"}</button>
                  ))}
                </div>
              )}
              {!terms.length && !shelf && recent.some((r) => byId[r]) && (
                <div className="edu-ref-recent">
                  <span>Last opened</span>
                  {recent.filter((r) => byId[r]).map((r) => (
                    <button key={r} onClick={() => openEntry(r)}>{byId[r].title}</button>
                  ))}
                </div>
              )}
              <ul className="edu-ref-entries">
                {hits.map((e) => (
                  <li key={e.id}>
                    <button onClick={() => openEntry(e.id)} data-testid={`edu-reference-item-${e.id}`}>
                      <span className="edu-ref-entry-shelf">{e.shelf}</span>
                      <b>{e.title}</b>
                      <span className="edu-ref-entry-sum">{e.summary}</span>
                    </button>
                  </li>
                ))}
                {!hits.length && <li className="edu-ref-none">Nothing in the reference matches “{q}”.</li>}
              </ul>
            </div>
          )}
      </div>
    </div>
  );
}

const LESSON_PATH = /^\/education\/lesson\/([0-9a-fA-F-]{36})\/?$/;

function lessonFromUrl() {
  try {
    const m = LESSON_PATH.exec(window.location.pathname);
    return m ? m[1] : null;
  } catch { return null; }
}

// ------------------------------------------------------------------- shell
export function Education({ user }) {
  const fromUrl = lessonFromUrl();
  const [screen, setScreen] = useState(fromUrl ? "lesson" : "home");
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [lessonId, setLessonId] = useState(fromUrl);
  const [quizSpec, setQuizSpec] = useState(null);

  const load = useCallback(() => {
    api("/me").then(setData).catch((e) => setErr(e.message));
  }, []);
  useEffect(() => { load(); }, [load]);

  const trackA = (data?.tracks || []).find((t) => t.key === "A");
  // Curriculum resolves the chapter to a lesson id before calling this, so a click always
  // lands on a real lesson.
  // ONE PLACE THAT MOVES BOTH the view state and the address, so they cannot disagree.
  // `replace` is for corrections that should not become a Back step.
  const goToLesson = useCallback((lid, { replace = false, push = true } = {}) => {
    setLessonId(lid);
    setQuizSpec(null);
    setScreen(lid ? "lesson" : "curriculum");
    if (!push) return;
    const path = lid ? `/education/lesson/${lid}` : "/";
    try {
      if (window.location.pathname === path) return;
      window.history[replace ? "replaceState" : "pushState"](null, "", path);
    } catch { /* history unavailable — the view still changed */ }
  }, []);
  const openLesson = (lid) => goToLesson(lid);

  // THE BACK BUTTON BEHAVES. Without this it left the course entirely, because the address
  // never changed when a lesson opened. `push: false` -- the browser has already moved.
  useEffect(() => {
    const onPop = () => goToLesson(lessonFromUrl(), { push: false });
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [goToLesson]);
  const openQuiz = (spec) => { setQuizSpec(spec); setScreen("quiz"); };

  // ONE CLICK FROM "I HAVE LOGGED IN" TO "I AM READING CHAPTER 1". Enrol, reload so the track
  // card carries real figures, then go straight to the curriculum rather than back to a screen
  // the student has already read.
  const [enrolling, setEnrolling] = useState(false);
  const enroll = async () => {
    if (enrolling || !trackA?.track_id) return;
    setEnrolling(true);
    try {
      await api("/enroll", { method: "POST",
                             body: JSON.stringify({ track_id: trackA.track_id }) });
      const fresh = await api("/me");
      setData(fresh);
      setScreen("curriculum");
    } catch (e) {
      setErr(e.message);
    } finally {
      setEnrolling(false);
    }
  };

  // THE REFERENCE PANEL is for enrolled students (the route is gated the same way). R opens it
  // from anywhere except a text field, so typing an R into an answer never pops it open.
  const [refOpen, setRefOpen] = useState(false);
  const canReference = !!(trackA && trackA.enrolled !== false && data?.enrollment);
  const closeRef = useCallback(() => setRefOpen(false), []);
  useEffect(() => {
    if (!canReference) return undefined;
    const onKey = (e) => {
      if (e.key !== "r" && e.key !== "R") return;
      if (e.ctrlKey || e.metaKey || e.altKey || e.defaultPrevented) return;
      const t = e.target;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      e.preventDefault();
      setRefOpen(true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [canReference]);

  let content;
  if (err) content = <div className="edu-body edu-error">{err}</div>;
  else if (!data) content = <div className="edu-body edu-loading"><Loader2 className="spin" /></div>;
  // BACK RETURNS TO WHERE THE QUIZ CAME FROM, ADDRESS INCLUDED (2026-09-28). It used to set the
  // view to the curriculum and leave the address on the lesson, so any reload, a phone reloading
  // the tab or the browser's Forward reopened the chapter: "Back" went forward. A chapter quiz is
  // opened from a lesson and goes back to it; the final exam is opened from the curriculum.
  else if (screen === "quiz" && quizSpec) {
    const toCurriculum = () => { load(); goToLesson(null, { replace: true }); };
    content = <Quiz spec={quizSpec}
      onBack={quizSpec.fromLesson ? () => { load(); goToLesson(quizSpec.fromLesson, { replace: true }); }
                                  : toCurriculum}
      onCurriculum={toCurriculum}
      onReference={canReference ? () => setRefOpen(true) : null}
      backLabel={quizSpec.fromLesson ? "Back to lesson" : "Back to curriculum"} />;
  }
  else if (screen === "lesson" && lessonId)
    content = <LessonPlayer lessonId={lessonId}
      onBack={() => goToLesson(null)} openQuiz={openQuiz} reload={load} />;
  else if (screen === "curriculum")
    content = <Curriculum trackId={trackA?.track_id} openLesson={openLesson} openQuiz={openQuiz} />;
  else if (screen === "record") content = <MyRecord />;
  // Anything else is home. There used to be a "Phase 2" placeholder page here for the rail
  // entries that are now gone; an unknown screen id lands somewhere real instead.
  else
    content = <MyLearning data={data} go={setScreen} openLesson={openLesson}
                          onEnroll={enroll} enrolling={enrolling} />;

  return (
      <div className="edu-app">
        <Rail screen={screen} go={(s) => { setQuizSpec(null); setScreen(s); }} user={user}
              onReference={canReference ? () => setRefOpen(true) : null} />
        <div className="edu-main">{content}</div>
        <ReferencePanel open={refOpen} onClose={closeRef} />
      </div>
  );
}

export default Education;
